import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal, Space } from 'antd';
import { useOneButtonStore, COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import { selectedQuote } from './FinancingGridCard';
import SolarQuoteDetails, { SolarDetails } from './SolarQuoteDetails';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import { AnyMxRecord } from 'dns';
import RoofQuoteDetails from './RoofQuoteDetails';
import RRQuoteDetails from './RRQuoteDetails';
import {
    buildQuoteFormTabs,
    columnIncludesBattery,
    columnIncludesRoof,
    columnIncludesRR,
    columnIncludesSolar,
    normalizeQuoteProductForModal,
} from './quoteCreationProducts';
import { BatteryQuoteDetails } from './BatteryQuoteDetails';
import { effectiveQuoteGroupIdForSrPack } from '../../../../oneButton/quoteGroupContractRules';
import DashboardModal from './DashboardModal';
import HoverLogoMark from './HoverLogoMark';
import EnfinEligibleProductModal from './EnfinEligibleProductModal';
import LightreachEligibleProductModal from './LightreachEligibleProductModal';
import {
    shouldFetchEnfinBatteryPricing,
    shouldShowEnfinEligibleProducts,
} from './enfinEligibleProductUtils';
import {
    getLightreachSolarSyncLeadIdForEligibleProducts,
    normalizeLightreachPricingRows,
    resolveLightreachBatteryPricingAmount,
    shouldShowLightreachEligibleProducts,
} from './lightreachEligibleProductUtils';
import { FolderOpen, Sun } from '@phosphor-icons/react';
import './oneButton-css/GenerateQuoteModal.css';
import designServices from '../../../../services/oneButtonServices/designServices';
import {
    evaluateDesignLossesMismatchForQuote,
    showDesignLossesMismatchModal,
} from './designLossesFinanceGate';
import { useOptionalProductsFinanceHubContext } from './ProductsFinanceHubContext';
import {
    findActiveOpportunityProductByType,
    getQuoteOpportunityProductValidationMessage,
} from './buildGenerateQuoteFromFinanceLine';
import { resolveProductTypeIdFromCategory } from './productsFinanceHubHelpers';
import { findSyncStatusRowForProduct } from './partnerSyncConfig';
import {
    getLightreachLeadIdFromOpportunity,
    resolveLightreachBatteryLeaseLeadId,
} from '../../../../helpers/lightReachLeadId';
import {
    batteryTermAprOrNull,
    productIdFromLrBoOffer,
    year1TotalMonthlyFromLrBoOffer,
} from '../../../../helpers/lightReachBatteryMonthly';

/** Trinity save: RoofOnlyAdaptor persists `RoofOnlyPackage.Offer_Id`; combined roof uses `RoofQuote.Offer_Id`. */
function resolveRoofOfferIdFromDetails(rd: Record<string, unknown> | null | undefined): string {
    if (!rd) return '';
    return String(rd.Offer_Id ?? rd.financePartnerProductId ?? rd.Finance_Partner_Product_Id ?? '').trim();
}

function applyGoodLeapRoofOfferFields(payload: Record<string, any>, rd: Record<string, unknown> | null | undefined): void {
    const partner = String(rd?.financePartnerName ?? '').replace(/\s+/g, '').toLowerCase();
    const method = String(rd?.contract_Type ?? '').trim().toLowerCase();
    const isGoodLeapLoan = partner === 'goodleap' && !!method && !method.includes('cash');
    const offerId = resolveRoofOfferIdFromDetails(rd);

    const clearOfferFields = (obj: Record<string, any> | undefined) => {
        if (!obj) return;
        delete obj.Offer_Id;
        delete obj.Finance_Partner_Product_Id;
    };

    if (!isGoodLeapLoan || !offerId) {
        clearOfferFields(payload.RoofQuote);
        clearOfferFields(payload.RoofOnlyPackage);
        return;
    }

    payload.RoofQuote = payload.RoofQuote || {};
    payload.RoofQuote.Offer_Id = offerId;
    payload.RoofQuote.Finance_Partner_Product_Id = offerId;
    payload.RoofOnlyPackage = payload.RoofOnlyPackage || {};
    payload.RoofOnlyPackage.Offer_Id = offerId;
    payload.RoofOnlyPackage.Finance_Partner_Product_Id = offerId;
}

export interface QuoteFormState {
    pricingMethod: string;
    apr: string;
    escalator: string;
    term: string;
    grossPpw: string;
    downPayment: string;
    monthly: number;
}

function quoteIdFromSaveResults(results: PromiseSettledResult<unknown>[]): string | undefined {
    for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        const value = result.value as Record<string, unknown> | null;
        const raw = value?.Message ?? value?.message ?? value?.quoteId ?? value?.QuoteId;
        const numeric = Number(raw);
        if (Number.isFinite(numeric) && numeric > 0) return String(Math.trunc(numeric));
        const id = String(raw ?? '').trim();
        if (id) return id;
    }
    return undefined;
}

export interface GenerateQuoteModalProps {
    confirmQuoteCellKey: selectedQuote | null;
    quoteForm: QuoteFormState | null;
    priceQuote: any;
    setConfirmQuoteCellKey: React.Dispatch<React.SetStateAction<selectedQuote | null>>;
    setQuoteForm: React.Dispatch<React.SetStateAction<QuoteFormState | null>>;
    updateQuoteVersion: (quoteId?: string) => void;
    noticeApi?: any;
    rcomendedQuote?: boolean;
}

export interface OpportunityInterestObject {
    Solar: boolean,
    Battery: boolean,
    BatteryOnly: boolean,
    Roof: boolean,
    RoofOnly: boolean,
    RRR: boolean,
    RR: boolean
}

export interface SolarObject {
    IsDomestic: boolean,
    SolarPackage: boolean,
    FinancePartnerId: number,
    Total_Amount: number,
    Deposit_Amount: any,
    ContractType: string,
    Tax: number,
    DownPayment: number,
    EPC: number,
    Escalator: string,
    PPW: number,
    Rate: any,
    Rebate: number,
    TermYears: number,
    PricingMethod: string,
    PricingValue: number,
    ITC_Amount: any,
    Monthly_Finance_Payment_NoITCPayback: any,
    Rebate_Method: string,
    Rebate_Amount_PerWatt: number,
    Adder_Price: number,
    Desired_PPW_Net: number,
    Finance_Partner_Product_Id: string,
    IsACH: boolean,
    Product_Type: string,
    Financed_Amount: any,
    AmountOnContractCompletion: number,
    AmountOnPhysicalInstallCompletion: number,
    AmountOnPhysicalInstallStart: number,
    AmountOnWorkCompletion: any,
    Finance_partner_contract_Type: string,
    Offer_Id: string,
    DealerFee:number;
}

export interface nonSolarObject {
    SolarPackage: boolean,
}

type FooterMessage = {
    type: 'error' | 'success';
    messages: string[];
};

const VALIDATION_MESSAGE_SEP = '\n';

const normalizeFooterMessages = (input: string | string[]): string[] => {
    const raw = Array.isArray(input) ? input : [input];
    return [
        ...new Set(
            raw
                .flatMap((message) => String(message ?? '').split(VALIDATION_MESSAGE_SEP))
                .map((message) => message.trim())
                .filter(Boolean),
        ),
    ];
};

const toFooterMessage = (type: FooterMessage['type'], input: string | string[]): FooterMessage => ({
    type,
    messages: normalizeFooterMessages(input),
});

const LIGHTREACH_SYNC_REQUIRED_MESSAGE =
    'The Utility infomation or System design has changed since the last update to Lightreach, but these changes have not yet been synced. Please return to the Design page, click Save, and confirm that the changes are synced to Lightreach. This step ensures that your new quote reflects the correct system size and provides accurate financial data.';

type LightreachEligibleProductData = Record<string, any>;
type LightreachPriceCapsData = Record<string, any>;

interface SolarMasterSyncValues {
    enabled: boolean;
    termApr?: string;
    pricingMethod?: string;
    escalator?: string;
}

export interface priceQuote {
    SalesForce_Id: string,
    Opportunity_Id: number,
    apr: string[],
    RoofSquareOverride: AnyMxRecord,
    Cloned_Quote_Id: number,
    InstallationState: string,
    PriceTier: null,
    SfdcSyncValue: string,
    TotalSaleAmount?: number,
    TotalCommissionableAmount: any,
    PurchaseMethod: any,
    RoofFinancingPartner: any,
    RoofFinanceMethod: any,
    RoofFinanceTerm: any,
    RoofCostType: any,
    RoofCommissionTier: any,
    LowTotal: number,
    RR_Price: any,
    Gross_Sales_Price_Per_Watt: any,
    Dealer_Fee_Percentage: number,
    Dealer_Epc: number,
    Monthly_Finance_Partner_Payment: any,
    Offer_Id: any,
    Production_rate: number,
    Low_Slope_Override: any,
    Normal_Slope_Override: any,
    LowSlope: any,
    NormalSlope: any,
    RR_System_Size: any,
    RoofProductId: any,
    RoofOnlyProductId: any,
    BatteryProductId: any,
    BatteryOnlyProductId: any,
    SolarProductId: any,
    RRProductId: any,


    OpportunityInterest: OpportunityInterestObject,

    Solar: SolarObject | nonSolarObject,

    Battery: any,

    BatteryOnlyPackage: any,

    RR: any,

    RoofQuote: any,

    RoofOnlyPackage: any,

    RoofOnly: any

    Roof: any

    RoofAdders: any[],
    BatteryAdders: any[]

    /** Hover estimate id from Convert. `0` when this quote is not Hover-backed. */
    hoverEstimateID?: string | number | null
}

/** Trinity `GenerateQuoteComponent.checkProductTypeFinancePartner` — maps FinancePartnerId on the save payload to `Finance_Partner` from opportunity products. */
function checkProductTypeFinancePartner(obj: any, productType: string, opportunityDetails: any): string {
    const pt = String(productType || '');
    let id: number | undefined;
    if (pt.includes('S')) id = obj?.Solar?.FinancePartnerId;
    else if (pt.includes('R')) {
        id = obj?.RoofOnlyPackage?.FinancePartnerId ?? obj?.RoofQuote?.FinancePartnerId;
    } else if (pt.includes('B')) {
        id = obj?.Battery?.FinancePartnerId ?? obj?.BatteryOnlyPackage?.FinancePartnerId;
    }

    const products =
        opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    if (!Array.isArray(products) || id == null || Number.isNaN(Number(id))) return '';

    const row = products.find((p: any) => {
        const pid = Number(p.Finance_Partner_Id ?? p.FinancePartnerId ?? p.finance_Partner_Id);
        return Number(id) === pid;
    });
    return String(row?.Finance_Partner ?? row?.finance_Partner ?? '').trim();
}

function mapBatteryAddersForSave(batteryDetails: any) {
    const list = Array.isArray(batteryDetails?.addersList) ? batteryDetails.addersList : [];
    return list.map((ele: any) => ({
        adderSelect: ele.AdderSelect,
        AdderSelect: ele.AdderSelect,
        Adder_Cost: ele.AdderCost,
        amount: 0,
        pricing_Type: batteryDetails?.priceTier || 'Standard',
    }));
}

function financePartnerQuoteAddersFromBattery(batteryDetails: any, fallbackProductTypeId: number) {
    const list = Array.isArray(batteryDetails?.addersList) ? batteryDetails.addersList : [];
    const toInt32 = (value: unknown) => {
        const n = Number(value);
        return Number.isFinite(n) && n > 0 ? n : 0;
    };
    return list
        .filter((adder: any) => toInt32(adder.productTypeAdderID) > 0)
        .map((adder: any) => ({
            finance_Partner_Quote_Id: 0,
            product_Type_Adder_Id: toInt32(adder.productTypeAdderID),
            product_Type_Id: toInt32(adder.productTypeID) || toInt32(fallbackProductTypeId),
            unit_Price: adder.UnitPrice || 0,
            quantity: adder.AdderQuantity || 0,
            total: adder.AdderCost || 0,
        }))
        .filter((row: { product_Type_Id: number }) => row.product_Type_Id > 0);
}


/** Same field order as dashboard `linkedProductQuoteMetrics` / RetrievePriceQuote. */
function proposedSolarRateFromRetrieveQuote(
    ...sources: Array<Record<string, unknown> | null | undefined>
): number {
    const keys = ['ProposedRate', 'Proposed_Solar_Rate', 'ProposedSolarRate', 'SolarRate', 'MinRate'];
    for (const src of sources) {
        if (!src || typeof src !== 'object') continue;
        for (const key of keys) {
            const n = Number(src[key]);
            if (Number.isFinite(n) && n > 0) return n;
        }
    }
    return 0;
}

/** Commerce Hub may pass a sparse `priceQuote` prop; store holds full RetrievePriceQuote. */
function mergePriceQuoteForGenerateQuote(prop: any, store: any): any {
    const merged = { ...(store ?? {}), ...(prop ?? {}) };
    if (!Number(merged.ProposedRate)) {
        const proposed = proposedSolarRateFromRetrieveQuote(prop, store);
        if (proposed > 0) merged.ProposedRate = proposed;
    }
    return merged;
}

export const GenerateQuoteModal: React.FC<GenerateQuoteModalProps> = ({
    confirmQuoteCellKey,
    quoteForm,
    priceQuote,
    setConfirmQuoteCellKey,
    setQuoteForm,
    updateQuoteVersion,
    noticeApi,
    rcomendedQuote = false,
}) => {
    const { theme, selectedFinanceCells, financingGrid, priceQuoteDetails } = useOneButtonStore();
    const effectivePriceQuote = useMemo(
        () => mergePriceQuoteForGenerateQuote(priceQuote, priceQuoteDetails),
        [priceQuote, priceQuoteDetails],
    );
    const [loaderCount, setLoaderCount] = useState<number>(0);
    const [showLoadingOverlay, setShowLoadingOverlay] = useState<boolean>(false);
    const [solarDetails, setSolarDetails] = React.useState<SolarDetails | null>(null);
    const [batteryDetails, setBatteryDetails] = React.useState<any>(null);
    const batteryDetailsRef = useRef<any>(null);
    const applyBatteryDetails = (data: any) => {
        const prev = batteryDetailsRef.current;
        if (
            prev &&
            prev.valid === data?.valid &&
            prev.validationMessage === data?.validationMessage &&
            prev.grossSalesPrice === data?.grossSalesPrice &&
            prev.financedAmount === data?.financedAmount &&
            prev.financePartnerId === data?.financePartnerId &&
            prev.financeMethodName === data?.financeMethodName &&
            prev.termAndApr === data?.termAndApr &&
            prev.Escalator === data?.Escalator &&
            prev.price === data?.price &&
            prev.priceTier === data?.priceTier &&
            prev.Offer_Id === data?.Offer_Id &&
            prev.billableAdderCost === data?.billableAdderCost
        ) {
            batteryDetailsRef.current = data;
            return;
        }
        batteryDetailsRef.current = data;
        setBatteryDetails(data);
    };
    const [eligibleProduct, setEligibleProduct] = React.useState<string[]>([]);
    const [sunnovaAPIDetails, setSunnovaAPIDetails] = React.useState<any>(null);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const productTypeList = useOneButtonStore((s) => s.productTypeList);
    const financeHubCtx = useOptionalProductsFinanceHubContext();
    const roofDetails = useOneButtonStore((s) => s.roofDetails);
    const updateRoofDetails = useOneButtonStore((s) => s.updateRoofDetails);
    const rrDetails = useOneButtonStore((s) => s.rrDetails);
    const selectedHoverEstimate = useOneButtonStore((s) => s.selectedHoverEstimate);
    const priceQuoteOverrides = useOneButtonStore((s) => s.priceQuoteOverrides);
    const hoverJobId = useOneButtonStore((s) => s.hoverJobId);
    const utilityDetailsDirty = useOneButtonStore((s) => s.utilityDetailsDirty);
    const [footerMessage, setFooterMessage] = useState<FooterMessage | null>(null);
    const [quoteGroupId, setQuoteGroupId] = useState<any>(null);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [lightreachEligibleProductData, setLightreachEligibleProductData] = useState<LightreachEligibleProductData[]>([]);
    const [lightreachPriceCapsData, setLightreachPriceCapsData] = useState<LightreachPriceCapsData[]>([]);
    const [lightreachPriceCapsReady, setLightreachPriceCapsReady] = useState(false);
    const [lightreachBatteryLeaseLeadId, setLightreachBatteryLeaseLeadId] = useState<string | null>(null);
    /** Stacked above Generate Quote `DashboardModal` (10080); matches `ProductionsCard` force-sync confirm. */
    const [lightreachSyncRequiredOpen, setLightreachSyncRequiredOpen] = useState(false);
    const [enfinEligibleOpen, setEnfinEligibleOpen] = useState(false);
    const [lightreachEligibleOpen, setLightreachEligibleOpen] = useState(false);

    const changeOrderDetails = useOneButtonStore((s) => s.changeOrderDetails);

    const systemDesign = useOneButtonStore((s) => s.systemDesign);
    const systemDesignLosses = useOneButtonStore((s) => s.systemDesignLosses);

    const notifClass = `obd-notification obd-theme-${theme}`;



    const normalizeFinanceToken = (value: unknown): string =>
        String(value ?? '')
            .replace(/\s+/g, '')
            .trim()
            .toLowerCase();

    const setProductValidationFooter = (
        product: any,
        quotePartnerName: string | null | undefined,
        quoteMethod: string | null | undefined,
        productLabel: string,
    ): boolean => {
        const message = getQuoteOpportunityProductValidationMessage(
            product,
            quotePartnerName,
            quoteMethod,
            productLabel,
        );
        if (!message) return false;
        setFooterMessage(toFooterMessage('error', message));
        setIsGenerating(false);
        return true;
    };

    const resolveQuoteFormValidationMessages = (): string[] => {
        const product = String(confirmQuoteCellKey?.product ?? '').trim();
        if (!product) return [];

        const messages: string[] = [];
        const addMessages = (raw?: string, fallback?: string) => {
            const parts = normalizeFooterMessages(raw ? [raw] : []);
            if (parts.length) {
                parts.forEach((part) => {
                    if (!messages.includes(part)) {
                        messages.push(part);
                    }
                });
                return;
            }
            const trimmedFallback = fallback?.trim();
            if (trimmedFallback && !messages.includes(trimmedFallback)) {
                messages.push(trimmedFallback);
            }
        };

        const needsSolar = columnIncludesSolar(product);
        const needsBattery = columnIncludesBattery(product);
        const needsRoof = columnIncludesRoof(product);
        const needsRR = columnIncludesRR(product);

        if (needsSolar && solarDetails) {
            if (solarDetails.validationMessage?.trim()) {
                addMessages(solarDetails.validationMessage);
            } else if (!solarDetails.valid) {
                addMessages(undefined, 'Complete required Solar quote details before generating.');
            }
        }
        if (needsBattery && batteryDetails) {
            if (batteryDetails.validationMessage?.trim()) {
                addMessages(batteryDetails.validationMessage);
            } else if (!batteryDetails.valid) {
                addMessages(undefined, 'Complete required Battery quote details before generating.');
            }
        }
        if (
            needsRoof &&
            roofDetails &&
            (!roofDetails.valid ||
                !roofDetails.gross_Sales_Price ||
                roofDetails.gross_Sales_Price === '0' ||
                roofDetails.Roofsq === 0)
        ) {
            if (roofDetails.validationMessage?.trim()) {
                addMessages(roofDetails.validationMessage);
            } else {
                if(roofDetails.Roofsq === 0)
                    addMessages(undefined, 'Complete required Roof quote details before generating.');
                else if(!roofDetails.gross_Sales_Price ||
                    roofDetails.gross_Sales_Price === '0'){
                    addMessages(undefined, 'Valid total sales amount is required.');
                }
            }
        }
        if (needsRR && rrDetails) {
            if (rrDetails.validationMessage?.trim() && !rrDetails.valid) {
                addMessages(rrDetails.validationMessage);
            } else if (!rrDetails.valid) {
                addMessages(undefined, 'Complete required RR quote details before generating.');
            }
        }
        return messages;
    };

    const resolveQuoteFormValidationMessage = (): string | null => {
        const messages = resolveQuoteFormValidationMessages();
        return messages.length ? messages.join(VALIDATION_MESSAGE_SEP) : null;
    };

    const solarMasterValues = useMemo(() => ({
        termApr: String(solarDetails?.TermAndAPR ?? '').trim(),
        pricingMethod: String(solarDetails?.pricingmethodName ?? '').trim(),
        escalator: String(solarDetails?.Escalator ?? '').trim(),
        partner: normalizeFinanceToken(solarDetails?.financePartnerName),
        method: normalizeFinanceToken(solarDetails?.contract_Type),
    }), [solarDetails]);

    const batterySolarMasterSync = useMemo<SolarMasterSyncValues>(() => {
        const batteryPartner = normalizeFinanceToken(batteryDetails?.financePartnerName);
        const batteryMethod = normalizeFinanceToken(batteryDetails?.financeMethodName);
        const enabled =
            !!solarMasterValues.partner &&
            !!solarMasterValues.method &&
            solarMasterValues.partner === batteryPartner &&
            solarMasterValues.method === batteryMethod;
        return {
            enabled,
            termApr: solarMasterValues.termApr,
            pricingMethod: solarMasterValues.pricingMethod,
            escalator: solarMasterValues.escalator,
        };
    }, [batteryDetails, solarMasterValues]);

    const roofSolarMasterSync = useMemo<SolarMasterSyncValues>(() => {
        const roofPartner = normalizeFinanceToken(roofDetails?.financePartnerName);
        const roofMethod = normalizeFinanceToken(roofDetails?.contract_Type);
        const enabled =
            !!solarMasterValues.partner &&
            !!solarMasterValues.method &&
            solarMasterValues.partner === roofPartner &&
            solarMasterValues.method === roofMethod;

        return {
            enabled,
            termApr: solarMasterValues.termApr,
        };
    }, [roofDetails, solarMasterValues]);

    const showEnfinEligibleProductButton = useMemo(() => {
        if (!confirmQuoteCellKey || !shouldShowEnfinEligibleProducts(opportunityDetails, priceQuote)) {
            return false;
        }
        const product = String(confirmQuoteCellKey.product ?? '');
        return columnIncludesSolar(product);
    }, [confirmQuoteCellKey, opportunityDetails, priceQuote]);

    const showLightreachEligibleProductButton = useMemo(() => {
        if (!confirmQuoteCellKey || !shouldShowLightreachEligibleProducts(opportunityDetails)) {
            return false;
        }
        const product = String(confirmQuoteCellKey.product ?? '');
        return columnIncludesSolar(product) || columnIncludesBattery(product);
    }, [confirmQuoteCellKey, opportunityDetails]);

    const lightreachBatteryPricingAmount = useMemo(
        () => resolveLightreachBatteryPricingAmount(batteryDetails),
        [batteryDetails],
    );

    const enfinEligibleProductType = useMemo(() => {
        const product = String(confirmQuoteCellKey?.product ?? '');
        if (
            columnIncludesSolar(product) &&
            columnIncludesBattery(product) &&
            priceQuote?.Solar?.FinancePartnerId === priceQuote?.Battery?.FinancePartnerId
        ) {
            return 'SB';
        }
        return 'S';
    }, [confirmQuoteCellKey?.product, priceQuote]);

    const fetchEnfinBatteryPricing = useMemo(
        () => shouldFetchEnfinBatteryPricing(opportunityDetails, enfinEligibleProductType),
        [enfinEligibleProductType, opportunityDetails],
    );

    const opportunityIdForEnfinModal = useMemo(
        () => opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? null,
        [opportunityDetails],
    );


    const getQuoteGroupIdForGeneration = async (): Promise<number | null> => {
        updateLoaderCount(true);
        try {
            // V1: getQuoteGroupID → response.Data, then (parsed > 0 ? parsed + 1 : 1)
            const response: any = await quoteServices.getQuoteGroupID(opportunityDetails.Opportunity_Id);
            const apiGroupId = response?.Data ?? response?.data ?? response;
            const parsedGroupId = parseInt(String(apiGroupId), 10);
            return !isNaN(parsedGroupId) && parsedGroupId > 0 ? parsedGroupId + 1 : 1;
        } catch (error) {
            console.error('Failed to fetch quote group id:', error);
            setFooterMessage(toFooterMessage('error', 'Unable to initialize quote generation. Please try again.'));
            return null;
        } finally {
            updateLoaderCount(false);
        }
    };

    const handleOk = async () => {
        setIsGenerating(true);
        if (loaderCount > 0) {
            setIsGenerating(false);
            return;
        }
        const quoteProduct = confirmQuoteCellKey?.product;
        if (
            utilityDetailsDirty &&
            quoteProduct &&
            columnIncludesSolar(quoteProduct)
        ) {
            const msg =
                'Utility details have unsaved changes. Open Utility details, click Save, then create the quote.';
            setFooterMessage(toFooterMessage('error', msg));
            setIsGenerating(false);
            return;
        }

        const lossesMismatch = evaluateDesignLossesMismatchForQuote(
            systemDesignLosses,
            opportunityDetails,
            quoteProduct,
            financeHubCtx?.rows,
        );

        try {
            if (lossesMismatch.blocked) {
                showDesignLossesMismatchModal(
                    lossesMismatch.message ??
                        'Design losses do not match the selected finance partner.',
                    theme,
                    13200,
                );
                setIsGenerating(false);
                return;
            }

            const formValidationMessage = resolveQuoteFormValidationMessage();
            if (formValidationMessage) {
                setFooterMessage(
                    toFooterMessage('error', formValidationMessage ?? 'Complete all required quote details before generating.'),
                );
                setIsGenerating(false);
                return;
            }

            const quoteProductName = String(confirmQuoteCellKey?.product ?? '');
            if (columnIncludesRoof(quoteProductName)) {
                const liveRoof = useOneButtonStore.getState().roofDetails ?? roofDetails;
                const roofPartner = String(liveRoof?.financePartnerName ?? '').replace(/\s+/g, '').toLowerCase();
                const roofMethod = String(liveRoof?.contract_Type ?? '').trim().toLowerCase();
                if (roofPartner === 'goodleap' && roofMethod && !roofMethod.includes('cash')) {
                    if (!resolveRoofOfferIdFromDetails(liveRoof)) {
                        setFooterMessage(
                            toFooterMessage(
                                'error',
                                'GoodLeap Offer Id is still loading. Wait a moment after selecting term/APR, then try Generate again.',
                            ),
                        );
                        setIsGenerating(false);
                        return;
                    }
                }
            }

            let lightreachProducts = opportunityDetails.OpportunityProduct.filter((x: any) => x.Finance_Partner_Display_Name.toLocaleLowerCase() === 'lightreach');
                if(lightreachProducts && lightreachProducts.length > 0 && changeOrderDetails && changeOrderDetails.length > 0){
        
                    const lightReachData = changeOrderDetails.find((x: any) => x.Finance_Partner_Name.toLocaleLowerCase() == COMMON_CONSTANTS.LightreachPartnerName.toLocaleLowerCase());
                    
                    if (lightReachData) {
                        const syncList = useOneButtonStore.getState().financePartnerRawSyncStatuses ?? [];
                        const quotedType = normalizeQuoteProductForModal(confirmQuoteCellKey?.product);
                        const syncRow = quotedType
                            ? findSyncStatusRowForProduct(syncList, quotedType === 'Battery' ? 'Battery' : quotedType)
                            : undefined;
                        const utilityStillNeeded = !!lightReachData.Is_Utility_Updated && syncRow?.IsUtility !== true;
                        const designStillNeeded = !!lightReachData.Is_System_Design_Updated && syncRow?.IsLeadSystem !== true;
                        if (lightReachData.Is_Change_Order && (utilityStillNeeded || designStillNeeded)){
                            setLightreachSyncRequiredOpen(true);
                            setIsGenerating(false);
                            return;
                        } 
                        // else {
                        //     const oppId = opportunityDetails?.Opportunity_Id;
                        //     if (oppId != null && String(oppId).trim() !== '') {
                        //         updateLoaderCount(true);
                        //         try {
                        //             const designCheckMessage = await designServices.checkNewDesignExist(oppId);
                        //             if (designCheckMessage?.data?.Success) {
                        //                 setLightreachSyncRequiredOpen(true);
                        //                 setIsGenerating(false);
                        //                 return;
                        //             }
                        //         } catch {
                        //             setFooterMessage({
                        //                 type: 'error',
                        //                 text: 'Unable to verify system design before quote. Please try again.',
                        //             });
                        //             setIsGenerating(false);
                        //             return;
                        //         } finally {
                        //             updateLoaderCount(false);
                        //         }
                        //     }
                        // }
                    } 
                }
                
                const quoteProductRoute = normalizeQuoteProductForModal(confirmQuoteCellKey?.product);
                if (quoteProductRoute === 'Solar' && solarDetails?.valid) {
                    const oppProducts = opportunityDetails?.OpportunityProduct ?? [];
                    const solarProduct = findActiveOpportunityProductByType(oppProducts, 'Solar');
                    if (
                        setProductValidationFooter(
                            solarProduct,
                            solarDetails.financePartnerName,
                            solarDetails.contract_Type,
                            'Solar',
                        )
                    ) {
                        return;
                    }
                    solarDetails.financePartnerId = solarProduct.Finance_Partner_Id;
                    const groupId = await getQuoteGroupIdForGeneration();
                    if (groupId == null) return;
                    setQuoteGroupId(groupId);
                    GenerateQuote(groupId);
                } else if (quoteProductRoute === 'Roof' && roofDetails?.valid && !!roofDetails?.gross_Sales_Price && roofDetails?.gross_Sales_Price != "0" && roofDetails.Roofsq != 0) {
                    const oppProducts = opportunityDetails?.OpportunityProduct ?? [];
                    const roofProduct = findActiveOpportunityProductByType(oppProducts, 'Roof');
                    if (
                        setProductValidationFooter(
                            roofProduct,
                            roofDetails.financePartnerName,
                            roofDetails.contract_Type,
                            'Roof',
                        )
                    ) {
                        return;
                    }
                    roofDetails.financePartnerId = roofProduct.Finance_Partner_Id;
                    const groupId = await getQuoteGroupIdForGeneration();
                    if (groupId == null) return;
                    setQuoteGroupId(groupId);
                    GenerateQuote(groupId);
                } else if (quoteProductRoute === 'RR' && rrDetails?.valid) {
                    const oppProducts = opportunityDetails?.OpportunityProduct ?? [];
                    const rrProduct = findActiveOpportunityProductByType(oppProducts, 'RR');
                    if (
                        setProductValidationFooter(
                            rrProduct,
                            rrDetails.financePartnerName,
                            rrDetails.contract_Type,
                            'RR',
                        )
                    ) {
                        return;
                    }
                    rrDetails.financePartnerId = rrProduct.Finance_Partner_Id;
                    const groupId = await getQuoteGroupIdForGeneration();
                    if (groupId == null) return;
                    setQuoteGroupId(groupId);
                    GenerateQuote(groupId);
                } else if (quoteProductRoute === 'Battery' && batteryDetails?.valid) {
                    const oppProducts = opportunityDetails?.OpportunityProduct ?? [];
                    const batteryProduct = findActiveOpportunityProductByType(oppProducts, 'Battery');
                    if (
                        setProductValidationFooter(
                            batteryProduct,
                            batteryDetails.financePartnerName,
                            batteryDetails.financeMethodName,
                            'Battery',
                        )
                    ) {
                        return;
                    }
                    batteryDetails.financePartnerId = batteryProduct.Finance_Partner_Id;
                    const groupId = await getQuoteGroupIdForGeneration();
                    if (groupId == null) return;
                    setQuoteGroupId(groupId);
                    GenerateQuote(groupId);
                } else if (quoteProductRoute === 'Solar + Battery' && solarDetails?.valid && batteryDetails?.valid) {
                    const oppProducts = opportunityDetails?.OpportunityProduct ?? [];
                    const solarProduct = findActiveOpportunityProductByType(oppProducts, 'Solar');
                    const batteryProduct = findActiveOpportunityProductByType(oppProducts, 'Battery');
                    if (
                        setProductValidationFooter(
                            solarProduct,
                            solarDetails.financePartnerName,
                            solarDetails.contract_Type,
                            'Solar',
                        ) ||
                        setProductValidationFooter(
                            batteryProduct,
                            batteryDetails.financePartnerName,
                            batteryDetails.financeMethodName,
                            'Battery',
                        )
                    ) {
                        return;
                    }
                    solarDetails.financePartnerId = solarProduct.Finance_Partner_Id;
                    batteryDetails.financePartnerId = batteryProduct.Finance_Partner_Id;
                    const groupId = await getQuoteGroupIdForGeneration();
                    if (groupId == null) return;
                    setQuoteGroupId(groupId);
                    GenerateQuote(groupId);
                } else if (quoteProductRoute === 'Solar + Roof' && solarDetails?.valid && roofDetails?.valid && !!roofDetails?.gross_Sales_Price && roofDetails?.gross_Sales_Price != "0" && roofDetails.Roofsq != 0) {
                    const oppProducts = opportunityDetails?.OpportunityProduct ?? [];
                    const solarProduct = findActiveOpportunityProductByType(oppProducts, 'Solar');
                    const roofProduct = findActiveOpportunityProductByType(oppProducts, 'Roof');
                    if (
                        setProductValidationFooter(
                            solarProduct,
                            solarDetails.financePartnerName,
                            solarDetails.contract_Type,
                            'Solar',
                        ) ||
                        setProductValidationFooter(
                            roofProduct,
                            roofDetails.financePartnerName,
                            roofDetails.contract_Type,
                            'Roof',
                        )
                    ) {
                        return;
                    }
                    solarDetails.financePartnerId = solarProduct.Finance_Partner_Id;
                    roofDetails.financePartnerId = roofProduct.Finance_Partner_Id;
                    const groupId = await getQuoteGroupIdForGeneration();
                    if (groupId == null) return;
                    setQuoteGroupId(groupId);
                    GenerateQuote(groupId);
                } else if (quoteProductRoute === 'Battery + Roof' && batteryDetails?.valid && roofDetails?.valid && !!roofDetails?.gross_Sales_Price && roofDetails?.gross_Sales_Price != "0" && roofDetails.Roofsq != 0) {
                    const oppProducts = opportunityDetails?.OpportunityProduct ?? [];
                    const batteryProduct = findActiveOpportunityProductByType(oppProducts, 'Battery');
                    const roofProduct = findActiveOpportunityProductByType(oppProducts, 'Roof');
                    if (
                        setProductValidationFooter(
                            batteryProduct,
                            batteryDetails.financePartnerName,
                            batteryDetails.financeMethodName,
                            'Battery',
                        ) ||
                        setProductValidationFooter(
                            roofProduct,
                            roofDetails.financePartnerName,
                            roofDetails.contract_Type,
                            'Roof',
                        )
                    ) {
                        return;
                    }
                    batteryDetails.financePartnerId = batteryProduct.Finance_Partner_Id;
                    roofDetails.financePartnerId = roofProduct.Finance_Partner_Id;
                    const groupId = await getQuoteGroupIdForGeneration();
                    if (groupId == null) return;
                    setQuoteGroupId(groupId);
                    GenerateQuote(groupId);
                } else if((quoteProductRoute === 'Solar + Battery + Roof' || quoteProductRoute === 'Solar + Roof + Battery') 
                    && solarDetails?.valid && batteryDetails?.valid && roofDetails?.valid 
                    && !!roofDetails?.gross_Sales_Price 
                    && roofDetails?.gross_Sales_Price != "0" 
                    && roofDetails?.Roofsq != 0) {
                    const oppProducts = opportunityDetails?.OpportunityProduct ?? [];
                    const solarProduct = findActiveOpportunityProductByType(oppProducts, 'Solar');
                    const batteryProduct = findActiveOpportunityProductByType(oppProducts, 'Battery');
                    const roofProduct = findActiveOpportunityProductByType(oppProducts, 'Roof');
                    if (
                        setProductValidationFooter(
                            solarProduct,
                            solarDetails.financePartnerName,
                            solarDetails.contract_Type,
                            'Solar',
                        ) ||
                        setProductValidationFooter(
                            batteryProduct,
                            batteryDetails.financePartnerName,
                            batteryDetails.financeMethodName,
                            'Battery',
                        ) ||
                        setProductValidationFooter(
                            roofProduct,
                            roofDetails.financePartnerName,
                            roofDetails.contract_Type,
                            'Roof',
                        )
                    ) {
                        return;
                    }
                    solarDetails.financePartnerId = solarProduct.Finance_Partner_Id;
                    batteryDetails.financePartnerId = batteryProduct.Finance_Partner_Id;
                    roofDetails.financePartnerId = roofProduct.Finance_Partner_Id;
                    const groupId = await getQuoteGroupIdForGeneration();
                    if (groupId == null) return;
                    setQuoteGroupId(groupId);
                    GenerateQuote(groupId);
                } else {
                    const formValidationMessage = resolveQuoteFormValidationMessage();
                    setFooterMessage(
                        toFooterMessage(
                            'error',
                            formValidationMessage ?? 'Complete all required quote details before generating.',
                        ),
                    );
                    setIsGenerating(false);
                }
          } finally {
            setIsGenerating(false);
          }
        
       
    };



    const tabs = useMemo(() => {
        if (!confirmQuoteCellKey) return [];
        return buildQuoteFormTabs(confirmQuoteCellKey, selectedFinanceCells, financingGrid);
    }, [confirmQuoteCellKey, selectedFinanceCells, financingGrid]);
    let isLightReach = false;
    if(confirmQuoteCellKey?.partner) {
        isLightReach = confirmQuoteCellKey?.partner?.toLocaleLowerCase() === 'lightreach';
    } else if(confirmQuoteCellKey?.products && confirmQuoteCellKey?.products?.length > 0) {
        isLightReach = confirmQuoteCellKey?.products?.some((product: any) => product?.partner?.toLocaleLowerCase() === 'lightreach');
    }
    if (tabs.some((tab) => tab.quote?.partner?.toLocaleLowerCase() === 'lightreach')) {
        isLightReach = true;
    }
    const isSolarLightreach =
        tabs.some((tab) => tab.kind === 'solar' && tab.quote?.partner?.toLocaleLowerCase() === 'lightreach') ||
        !!confirmQuoteCellKey?.products?.some(
            (product: any) =>
                String(product?.product ?? '').toLowerCase() === 'solar' &&
                String(product?.partner ?? '').toLowerCase() === 'lightreach',
        );

    const resolveLightreachCapLeadId = async () => {
        const od = useOneButtonStore.getState().opportunityDetails ?? opportunityDetails;
        const opportunityId =
            priceQuote?.Opportunity_Id
            ?? od?.Opportunity_Id
            ?? od?.opportunity_Id
            ?? useOneButtonStore.getState().opportunityID;
        const batteryLeadId = await resolveLightreachBatteryLeaseLeadId(od, opportunityId);
        if (batteryLeadId) {
            setLightreachBatteryLeaseLeadId(batteryLeadId);
            return batteryLeadId;
        }
        if (isSolarLightreach) return getLightreachLeadIdFromOpportunity(od);
        return null;
    };

    const getLightreachData = () => {
        let disposed = false;
        const syncLightreachData = async () => {
            if (!isLightReach) {
                if (!disposed) {
                    setLightreachEligibleProductData([]);
                    setLightreachPriceCapsData([]);
                    setLightreachPriceCapsReady(true);
                }
                return;
            }
            const opportunityId =
                priceQuote?.Opportunity_Id
                ?? opportunityDetails?.Opportunity_Id
                ?? opportunityDetails?.opportunity_Id
                ?? useOneButtonStore.getState().opportunityID;
            const capLeadId = await resolveLightreachCapLeadId();
            if (disposed) return;

            if (!capLeadId) {
                setLightreachEligibleProductData([]);
                setLightreachPriceCapsData([]);
                setLightreachPriceCapsReady(true);
                return;
            }

            updateLoaderCount(true);
            try {
                // V1: GetPriceCaps whenever Battery (or Solar) is Lightreach. Do not pair it with
                // LightreachPricingByAccount — that API is solar-LR only and 400s on a battery lead.
                try {
                    const priceCaps = await quoteServices.getPriceCaps(capLeadId);
                    if (!disposed) setLightreachPriceCapsData(Array.isArray(priceCaps) ? priceCaps : []);
                } catch {
                    if (!disposed) setLightreachPriceCapsData([]);
                }

                if (isSolarLightreach && opportunityId) {
                    const od = useOneButtonStore.getState().opportunityDetails ?? opportunityDetails;
                    const solarLeadId = getLightreachSolarSyncLeadIdForEligibleProducts(od);
                    if (!solarLeadId) {
                        if (!disposed) setLightreachEligibleProductData([]);
                    } else {
                        try {
                            const eligibleData = await quoteServices.getLightreachPricingByAccount(
                                opportunityId,
                                solarLeadId,
                            );
                            if (!disposed) {
                                setLightreachEligibleProductData(normalizeLightreachPricingRows(eligibleData));
                            }
                        } catch {
                            if (!disposed) setLightreachEligibleProductData([]);
                        }
                    }
                } else if (!disposed) {
                    setLightreachEligibleProductData([]);
                }
            } finally {
                if (!disposed) {
                    setLightreachPriceCapsReady(true);
                    updateLoaderCount(false);
                }
            }
        };
        void syncLightreachData();
        return () => {
            disposed = true;
        };
    };

    const confirmQuoteCellIdentity = useMemo(
        () =>
            confirmQuoteCellKey
                ? `${confirmQuoteCellKey.product}::${confirmQuoteCellKey.partner ?? ''}`
                : '',
        [confirmQuoteCellKey?.product, confirmQuoteCellKey?.partner],
    );

    useEffect(() => {
        setFooterMessage(null);
        setLoaderCount(0);
        setIsGenerating(false);
        setLightreachSyncRequiredOpen(false);
        setLightreachPriceCapsReady(false);
        setLightreachBatteryLeaseLeadId(null);
        setSolarDetails(null);
        batteryDetailsRef.current = null;
        setBatteryDetails(null);
        updateRoofDetails(null);
        if (!confirmQuoteCellIdentity) return undefined;
        return getLightreachData();
    }, [confirmQuoteCellIdentity, updateRoofDetails]);

    useEffect(() => {
        if (!confirmQuoteCellKey) return;
        if (loaderCount > 0) return;
        const validationMessages = resolveQuoteFormValidationMessages();
        setFooterMessage((current) => {
            if (validationMessages.length > 0) {
                const next = toFooterMessage('error', validationMessages);
                if (
                    current?.type === 'error' &&
                    current.messages.length === next.messages.length &&
                    current.messages.every((message, index) => message === next.messages[index])
                ) {
                    return current;
                }
                return next;
            }
            return current?.type === 'error' ? null : current;
        });
    }, [confirmQuoteCellKey, solarDetails, batteryDetails, roofDetails, loaderCount, rrDetails]);

    useEffect(() => {
        if (process.env.NODE_ENV !== 'production') {
            console.debug('[GenerateQuoteModal] loaderCount:', loaderCount);
        }
    }, [loaderCount]);

    useEffect(() => {
        setShowLoadingOverlay(loaderCount > 0 || isGenerating);
    }, [loaderCount, isGenerating]);

    const loadingOverlayMessage = useMemo(() => {
        if (isGenerating) return 'Generating Quote…';
        return 'Loading quote form…';
    }, [isGenerating]);

    const summaryParts = useMemo(() => {
        const parts: string[] = [];
        for (const k of selectedFinanceCells) {
            const idx = k.indexOf('::');
            if (idx < 0) continue;
            parts.push(`${k.slice(0, idx)} / ${k.slice(idx + 2)}`);
        }
        return parts;
    }, [selectedFinanceCells]);


    const handleCancel = () => {
        setLightreachSyncRequiredOpen(false);
        setLoaderCount(0);
        setIsGenerating(false);
        setFooterMessage(null);
        setConfirmQuoteCellKey(null);
        setQuoteForm(null);
        if(!rcomendedQuote) updateQuoteVersion();

    };


    // Step 1
    const GenerateQuote = async (quoteGroupId: any) => {
        if (confirmQuoteCellKey?.product === 'Solar' && (!priceQuote.EPCCalc.SystemSizeValue || priceQuote.EPCCalc.SystemSizeValue === null
            || parseInt(priceQuote.EPCCalc.SystemSizeValue)) == 0) {
            setIsGenerating(false);
            noticeApi.info({
                message: `Please navigate to system design and save`,
                className: notifClass,
            });
            return;
        }



        // OBA-4736 Show the confirmation popup when light reach product didn't sync the utility and system design datas
        let message;
        // const changeOrderDetails = await this.masterService.updateChangeOrderDetailsObservable.pipe(take(1)).toPromise();
        // if (Array.isArray(changeOrderDetails)) {
        //     const lightReachData = changeOrderDetails.find((x: any) => x.Finance_Partner_Name == OpportunityConstant.CommonConstants.LightreachPartnerName);
        //     if (lightReachData) {
        //         if (lightReachData.Is_Change_Order && (lightReachData.Is_Utility_Updated | lightReachData.Is_System_Design_Updated))
        //         message = this.setConfirmationMessage()
        //         else message = await this.CheckNewDesignExistAPI();
        //     }
        // }

        if (confirmQuoteCellKey?.product === 'Solar' && (!solarDetails?.financePartnerName || !solarDetails?.contract_Type)) {
            setIsGenerating(false);
            updateLoaderCount(false);
            setFooterMessage(toFooterMessage('error', 'Solar is missing the finance partner or contract type'));
        } else {
            // GoodLeap and LightReach (solar or roof) require finalized oneDRAW system losses before quote generation.
            const normFinanceName = (s: string | undefined) => (s || '').replace(/\s+/g, '').toLowerCase();
            const solarGoodLeap =
                normFinanceName(solarDetails?.financePartnerName) === 'goodleap' &&
                !String(solarDetails?.contract_Type || '').toLowerCase().includes('sunnova');
            const solarLightReach = normFinanceName(solarDetails?.financePartnerName) === 'lightreach';
            const roofGoodLeap = normFinanceName(roofDetails?.financePartnerName) === 'goodleap';
            const roofLightReach = normFinanceName(roofDetails?.financePartnerName) === 'lightreach';
            const solarNeedsDesignLosses = solarGoodLeap || solarLightReach;
            const roofNeedsDesignLosses = roofGoodLeap || roofLightReach;
            if (solarNeedsDesignLosses || roofNeedsDesignLosses) {
                const designReadiness = await GetOneDrawDesignReadiness();
                const product = confirmQuoteCellKey?.product;
                const needsLossesForProduct = (product?.includes('Solar') && solarGoodLeap);
                if (needsLossesForProduct && !designReadiness.hasSystemLosses) {
                    const message = designReadiness.opportunityStage === "4: Contracted"
                        ? "Design losses are missing in oneBUTTON. Please review the “Design” page and confirm that a oneDRAW design is available. If a oneDRAW design exists, finalize the design in oneDRAW, then refresh the Design page in oneBUTTON. Once refreshed, you should see the “System Losses” button, and will be able to submit the quote to your finance partner. If you are unable to finalize the design, or if no oneDRAW design is available, please contact RSA via Chatter on the Opportunity in Salesforce. Once RSA creates a new oneDRAW design or finalizes the existing design, you will be able to proceed."
                        : "Design losses are missing in oneBUTTON. Please re-finalize the design in oneDRAW, then refresh and save the Design page. Once completed, you can proceed with Quote Generation and continue processing the Change Order.";
                    setFooterMessage(toFooterMessage('error', message));
                    setIsGenerating(false);
                    updateLoaderCount(false);
                    return;
                }
            }

            //Generate New quote-groupd Id

            // V1 generateQuotes case 'B' / split B: LightReach Battery Lease sends EPC + battery escalator (Excel K4/M4).
            const battery = batteryDetailsRef.current ?? batteryDetails;
            const batteryPartnerName = String(battery?.financePartnerName || '').trim();
            const batteryContractType = String(battery?.financeMethodName || '').trim();
            let bPricingMethod = String(battery?.pricingMethod || '').trim();
            let bEscalator: string | number = battery?.Escalator || '';
            if (batteryPartnerName === COMMON_CONSTANTS.LightreachPartnerName && batteryContractType === 'Battery Lease') {
                bPricingMethod = bPricingMethod || 'EPC';
                bEscalator = battery?.Escalator || '';
            }

            const tasks: Promise<any>[] = [];
            switch (normalizeQuoteProductForModal(confirmQuoteCellKey?.product)) {
                case 'Solar':
                    tasks.push((async () => {
                        const financialData = await getESSunnovaFinancialData(
                            "S",
                            quoteGroupId,
                            solarDetails?.financePartnerName,
                            (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                            (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                            (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                            (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                            (eligibleProduct?.length > 0) ? eligibleProduct : ''
                        );
                        return savePriceAndQuoteAPI("S", quoteGroupId, financialData);
                    })());
                    break;
                case 'Battery':
                    tasks.push((async () => {
                        const financialData = await getESSunnovaFinancialData(
                            'B',
                            quoteGroupId,
                            battery?.financePartnerName,
                            (battery?.financeMethodName) ? battery?.financeMethodName.trim() : '',
                            bPricingMethod,
                            bEscalator,
                            (battery?.termAndApr) ? battery?.termAndApr.trim() : '',
                            '' // Eligible Product
                        );
                        return savePriceAndQuoteAPI("B", quoteGroupId, financialData);
                    })());
                    break;
                case 'Roof':
                    tasks.push((async () => {
                        const financialData = await getESSunnovaFinancialData(
                            'R',
                            quoteGroupId,
                            roofDetails?.financePartnerName,
                            (roofDetails?.contract_Type) ? roofDetails?.contract_Type.trim() : '',
                            '', // Pricing Method
                            0,  // Escalator
                            (roofDetails?.TermAPR) ? roofDetails.TermAPR.trim() : '',
                            '' // Eligible Product
                        );
                        return savePriceAndQuoteAPI("R", quoteGroupId, financialData);
                    })());
                    break;
                case 'RR':
                    tasks.push((async () => {
                        const liveRR = useOneButtonStore.getState().rrDetails ?? rrDetails;
                        const financialData = await getESSunnovaFinancialData(
                            'RR',
                            quoteGroupId,
                            liveRR?.financePartnerName,
                            (liveRR?.contract_Type) ? String(liveRR.contract_Type).trim() : '',
                            (liveRR?.PricingMethod) ? String(liveRR.PricingMethod).trim() : '',
                            '',
                            '',
                            ''
                        );
                        return savePriceAndQuoteAPI('RR', quoteGroupId, financialData);
                    })());
                    break;
                case 'Solar + Battery':

                    const isSunNovaMatch = solarDetails?.financePartnerName == COMMON_CONSTANTS.SunnovaPartnerName && batteryDetails?.financePartnerName == COMMON_CONSTANTS.SunnovaPartnerName ;
                    const isLightreachMatch = solarDetails?.financePartnerName == COMMON_CONSTANTS.LightreachPartnerName  && batteryDetails?.financePartnerName == COMMON_CONSTANTS.LightreachPartnerName;
                    const isEnfinMatch = solarDetails?.financePartnerName == COMMON_CONSTANTS.EnfinPartnerName && batteryDetails?.financePartnerName == COMMON_CONSTANTS.EnfinPartnerName;
                    const isGoodLeapMatch = (solarDetails?.financePartnerName == COMMON_CONSTANTS.GoodLeapPartnerName && batteryDetails?.financePartnerName == COMMON_CONSTANTS.GoodLeapPartnerName);
                    const isContractTypeMatch = solarDetails?.contract_Type?.trim() == batteryDetails?.financeMethodName?.trim();
                    if ((isSunNovaMatch || isLightreachMatch || isEnfinMatch) && isContractTypeMatch) {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'SB',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("SB", quoteGroupId, financialData);
                        })());
                    } else if (isGoodLeapMatch && isContractTypeMatch) {
      
                  
                        if (solarDetails?.contract_Type === 'Loan' || solarDetails?.contract_Type === 'Loan Storage') {
                            tasks.push((async () => {
                                const financialData = await getESSunnovaFinancialData(
                                    'SB',
                                    quoteGroupId,
                                    solarDetails?.financePartnerName,
                                    (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                    (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                    (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                    (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                    (eligibleProduct?.length > 0) ? eligibleProduct : ''
                                );
                                return savePriceAndQuoteAPI("SB", quoteGroupId, financialData);
                            })());
                        } else {
                            tasks.push(calculateMothlyPaymentSB(quoteGroupId));
                        }
                    } 
                    else {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'B',
                                quoteGroupId,
                                battery?.financePartnerName,
                                (battery?.financeMethodName) ? battery?.financeMethodName.trim() : '',
                                bPricingMethod,
                                bEscalator,
                                (battery?.termAndApr) ? battery?.termAndApr.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("B", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                "S",
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("S", quoteGroupId, financialData);
                        })());
                    }
                    break;
                case 'Solar + Roof':
                    if (String(solarDetails?.financePartnerName || '').trim().toLowerCase() === 'cash'
                        || String(solarDetails?.contract_Type || '').trim() !== String(roofDetails?.contract_Type || '').trim()) {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'S',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("S", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'R',
                                quoteGroupId,
                                roofDetails?.financePartnerName,
                                (roofDetails?.contract_Type) ? roofDetails?.contract_Type.trim() : '',
                                '', // Pricing Method
                                0,  // Escalator
                                (roofDetails?.TermAPR) ? roofDetails.TermAPR.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("R", quoteGroupId, financialData);
                        })());
                    } else {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'SR',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("SR", quoteGroupId, financialData);
                        })());
                    }
                    break;
                case 'Battery + Roof':
                    // Same pattern as Solar + Battery / Solar + Roof: one combined BR call when finance lines align; otherwise two saves (B then R).
                    if (String(batteryDetails?.financePartnerName || '').trim().toLowerCase() === 'cash'
                        || String(roofDetails?.financePartnerName || '').trim().toLowerCase() === 'cash'
                        || String(batteryDetails?.financeMethodName || '').trim() !== String(roofDetails?.contract_Type || '').trim()) {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'B',
                                quoteGroupId,
                                battery?.financePartnerName,
                                (battery?.financeMethodName) ? battery?.financeMethodName.trim() : '',
                                bPricingMethod,
                                bEscalator,
                                (battery?.termAndApr) ? battery?.termAndApr.trim() : '',
                                ''
                            );
                            return savePriceAndQuoteAPI("B", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'R',
                                quoteGroupId,
                                roofDetails?.financePartnerName,
                                (roofDetails?.contract_Type) ? roofDetails?.contract_Type.trim() : '',
                                '',
                                0,
                                (roofDetails?.TermAPR) ? roofDetails.TermAPR.trim() : '',
                                ''
                            );
                            return savePriceAndQuoteAPI("R", quoteGroupId, financialData);
                        })());
                    } else {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'BR',
                                quoteGroupId,
                                battery?.financePartnerName,
                                (battery?.financeMethodName) ? battery?.financeMethodName.trim() : '',
                                bPricingMethod,
                                bEscalator,
                                (battery?.termAndApr) ? battery?.termAndApr.trim() : '',
                                ''
                            );
                            return savePriceAndQuoteAPI("BR", quoteGroupId, financialData);
                        })());
                    }
                    break;
                case 'Solar + Battery + Roof':
                case 'Solar + Roof + Battery':
                    if (solarDetails?.contract_Type === batteryDetails?.financeMethodName 
                        && solarDetails?.financePartnerName === batteryDetails?.financePartnerName 
                        && solarDetails?.financePartnerName !== "Cash") {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'SB',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("SB", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'R',
                                quoteGroupId,
                                roofDetails?.financePartnerName,
                                (roofDetails?.contract_Type) ? roofDetails?.contract_Type.trim() : '',
                                '', // Pricing Method
                                0,  // Escalator
                                (roofDetails?.TermAPR) ? roofDetails.TermAPR.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("R", quoteGroupId, financialData);
                        })());
                    } else if (!(String(solarDetails?.financePartnerName || '').trim().toLowerCase() === 'cash'
                        || String(solarDetails?.contract_Type || '').trim() !== String(roofDetails?.contract_Type || '').trim())) {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'SR',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("SR", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'B',
                                quoteGroupId,
                                battery?.financePartnerName,
                                (battery?.financeMethodName) ? battery?.financeMethodName.trim() : '',
                                bPricingMethod,
                                bEscalator,
                                (battery?.termAndApr) ? battery?.termAndApr.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("B", quoteGroupId, financialData);
                        })());
                    } else {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'S',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("S", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'R',
                                quoteGroupId,
                                roofDetails?.financePartnerName,
                                (roofDetails?.contract_Type) ? roofDetails?.contract_Type.trim() : '',
                                '', // Pricing Method
                                0,  // Escalator
                                (roofDetails?.TermAPR) ? roofDetails.TermAPR.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("R", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'B',
                                quoteGroupId,
                                battery?.financePartnerName,
                                (battery?.financeMethodName) ? battery?.financeMethodName.trim() : '',
                                bPricingMethod,
                                bEscalator,
                                (battery?.termAndApr) ? battery?.termAndApr.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("B", quoteGroupId, financialData);
                        })());
                    }
                    break;
                case 'Solar + RRR':
                    if (String(solarDetails?.financePartnerName || '').trim().toLowerCase() === 'cash'
                        || String(solarDetails?.contract_Type || '').trim() !== String(roofDetails?.contract_Type || '').trim()) {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'S',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("S", quoteGroupId, financialData);
                        })());
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'R',
                                quoteGroupId,
                                roofDetails?.financePartnerName,
                                (roofDetails?.contract_Type) ? roofDetails?.contract_Type.trim() : '',
                                '', // Pricing Method
                                0,  // Escalator
                                (roofDetails?.TermAPR) ? roofDetails.TermAPR.trim() : '',
                                '' // Eligible Product
                            );
                            return savePriceAndQuoteAPI("R", quoteGroupId, financialData);
                        })());
                    } else {
                        tasks.push((async () => {
                            const financialData = await getESSunnovaFinancialData(
                                'SR',
                                quoteGroupId,
                                solarDetails?.financePartnerName,
                                (solarDetails?.contract_Type) ? solarDetails?.contract_Type.trim() : '',
                                (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                                (solarDetails?.Escalator) ? solarDetails?.Escalator : 0,
                                (solarDetails?.TermAndAPR) ? solarDetails?.TermAndAPR.trim() : '',
                                (eligibleProduct?.length > 0) ? eligibleProduct : ''
                            );
                            return savePriceAndQuoteAPI("SR", quoteGroupId, financialData);
                        })());
                    }
                    break;
                default:
                    if (normalizeQuoteProductForModal(confirmQuoteCellKey?.product) === 'Roof') updateLoaderCount(false);
            }

            if (tasks.length > 0) {
                setIsGenerating(true);
                try {
                    const results = await Promise.allSettled(tasks);
                    const hadSuccess = results.some((r) => r.status === 'fulfilled');
                    if (hadSuccess) {
                        postSavePriceAndQuoteOperations(quoteIdFromSaveResults(results));
                    } 
                } catch (error) {
                    console.error("Error generating quotes:", error);
                } finally {
                    setIsGenerating(false);
                }
            }
        }
    }

    /**
     * GoodLeap Solar + Battery (non-Loan): combined purchase price → partner monthly payment,
     * then SB quote save. Mirrors Trinity `GenerateQuoteComponent.calculateMothlyPaymentSB`.
     */
    async function calculateMothlyPaymentSB(quoteGroupId: any) {
        const purchasePrice =
            Number(solarDetails?.TotalSaleAmount) +
            Number(batteryDetails?.financedAmount ?? batteryDetails?.Financed_Amount ?? 0);
        const payload = {
            opportunityId: priceQuote.Opportunity_Id,
            offerId: solarDetails?.Offer_Id,
            systemSize: priceQuote.EPCCalc.SystemSizeValue,
            estimatedAnnualProduction: Number(priceQuote.EPCCalc.ProductionSizeValue) * 1000,
            kWhRate: 0,
            monthlyPayment: 0,
            dollarPerWatt: 0,
            purchasePrice: Number(purchasePrice.toFixed(2)),
        };

        try {
            updateLoaderCount(true);
            const res = await quoteServices.updateGoodLeapTotalSaleAmount(payload);
            if (!res.description) {
                if (solarDetails) {
                    solarDetails.Monthly_Finance_Partner_Payment = res.firstYearMonthlyPayment;
                    solarDetails.productionRate = res.productionRate;
                    setSolarDetails({ ...solarDetails });
                }
                if (batteryDetails) {
                    batteryDetails.rate =
                        Number(res.productionRate) - Number(solarDetails?.SolarProductionRate ?? 0);
                    setBatteryDetails({ ...batteryDetails });
                }
                const financialData = await getESSunnovaFinancialData(
                    'SB',
                    quoteGroupId,
                    solarDetails?.financePartnerName,
                    solarDetails?.contract_Type?.trim() || '',
                    solarDetails?.pricingmethodName?.trim() || '',
                    solarDetails?.Escalator || 0,
                    solarDetails?.TermAndAPR?.trim() || '',
                    eligibleProduct?.length > 0 ? eligibleProduct : ''
                );
                return savePriceAndQuoteAPI('SB', quoteGroupId, financialData);
            }

            setFooterMessage(
                toFooterMessage('error', `${res.description}. Please refresh the design and try again`),
            );
            setIsGenerating(false);
            throw new Error(res.description);
        } catch (error) {
            console.error('Error in retriveing monthly payment for Goodleap', error);
            throw error;
        } finally {
            updateLoaderCount(false);
        }
    }

    // Step 2
    function getESSunnovaFinancialData(productType: any, quoteGroupId: any, financePartnerselected: string | undefined,
        contract_Type: any, pricingMethod: any, escalator: any, termApr: any, eligibleProducts: any): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            let PriceQuotes = {
                "ExcelFileName": "Sunnova Financial Data",
                "SheetName": "SVA Financing Options",
                "InputValues": [{
                    "Type": "String",
                    "CellNumber": "B4",
                    "CellValue": priceQuote.InstallationState
                },
                {
                    "Type": "String",
                    "CellNumber": "C4",
                    "CellValue": productType == 'RR' ? 'R&R' : productType
                },
                {
                    "Type": "String",
                    "CellNumber": "D4",
                    "CellValue": financePartnerselected
                },
                {
                    "Type": "String",
                    "CellNumber": "F4",
                    "CellValue": contract_Type
                },
                {
                    "Type": "String",
                    "CellNumber": "K4",
                    "CellValue": pricingMethod
                },
                {
                    "Type": "String",
                    "CellNumber": "M4",
                    "CellValue": escalator
                },
                {
                    "Type": "String",
                    "CellNumber": "E8",
                    "CellValue": priceQuote.UtilityCompanyName || ''
                },
                {
                    "Type": "String",
                    "CellNumber": "C8",
                    "CellValue": (eligibleProducts?.length > 0) ? eligibleProducts : ''
                }
                ],
                "OutputCells": ["T4", "U4", "V4", "W4", "X4", "Y4", "Z4", "AA4", 'AB4']
            };

            if (String(termApr ?? '').includes('ACH')) {
                if (String(termApr).includes('w/ ACH')) {
                    PriceQuotes.InputValues.push({
                        "Type": "String",
                        "CellNumber": "O4",
                        "CellValue": termApr.replace('w/ ACH', '').trim()
                    })
                    PriceQuotes.InputValues.push({
                        "Type": "String",
                        "CellNumber": "L11",
                        "CellValue": "Yes"
                    })
                } else {
                    PriceQuotes.InputValues.push({
                        "Type": "String",
                        "CellNumber": "O4",
                        "CellValue": termApr.replace('w/o ACH', '').trim()
                    })
                    PriceQuotes.InputValues.push({
                        "Type": "String",
                        "CellNumber": "L11",
                        "CellValue": "No"
                    })
                }
            } else {
                PriceQuotes.InputValues.push({
                    "Type": "String",
                    "CellNumber": "O4",
                    "CellValue": termApr
                })
                PriceQuotes.InputValues.push({
                    "Type": "String",
                    "CellNumber": "L11",
                    "CellValue": "Yes"
                })
            }
            PriceQuotes.InputValues.push({
                "Type": "String",
                "CellNumber": "E8",
                "CellValue": priceQuote.ExcelUtilityCompanyName || ''
            })
            updateLoaderCount(true);
            masterServices.cascadingDropDown(PriceQuotes).then((data: any) => {
                let sunnovaAPIDetailsTemp: any = {};
                if (data[0] != null) sunnovaAPIDetailsTemp.ID = data[0].CellValue;
                if (data[1] != null) sunnovaAPIDetailsTemp.Name = data[1].CellValue;
                if (data[2] != null) sunnovaAPIDetailsTemp.ContractID = data[2].CellValue;
                if (data[3] != null) sunnovaAPIDetailsTemp.TermLength = (financePartnerselected === 'Sunlight') ? (Number(data[3].CellValue) / 12) : data[3].CellValue;
                if (data[4] != null) sunnovaAPIDetailsTemp.APR = data[4].CellValue;
                if (data[5] != null) sunnovaAPIDetailsTemp.DealerFee = isNaN(Number(data[5].CellValue)) ? 0 : Number(data[5].CellValue); //Handled No Match Found case
                if (data[6] != null) sunnovaAPIDetailsTemp.PricingMethod = data[6].CellValue;
                if (data[7] != null) sunnovaAPIDetailsTemp.PaymentEscalator = data[7].CellValue;
                if (data[8] != null) sunnovaAPIDetailsTemp.SfdcSyncValue = data[8].CellValue;
                resolve(sunnovaAPIDetailsTemp);
                updateLoaderCount(false);
            }).catch((err: any) => {
                reject(err);
                updateLoaderCount(false);
            });
        });
    }

    // Step 3
    const savePriceAndQuoteAPI = (productType: any, quoteGroupId: any, sunnovaAPIDetails: any): Promise<any> => {
        return new Promise((resolve, reject) => {
            //For a new quote with Same finance Method
            let quoteIsMultiContract = false;
            // if (this.battery && this.battery.BatteryAdder) {
            //     if (this.battery.BatteryAdder.length == 2) {
            //         this.battery.BatteryAdder[1].amount = 0;
            //     }
            // }
            /**Solar details */

            let OpportunityInterest = { Solar: false, Battery: false, BatteryOnly: false, Roof: false, RoofOnly: false, RRR: false, RR: false }
            switch (productType) {
                case 'B':
                    OpportunityInterest.BatteryOnly = true;
                    break;
                case 'R':
                    if (opportunityDetails.OpportunityInterest?.RRR) OpportunityInterest.RRR = true;
                    else OpportunityInterest.RoofOnly = true;
                    break;
                case 'S':
                    OpportunityInterest.Solar = true;
                    break;
                case 'SB':
                    OpportunityInterest.Solar = true;
                    OpportunityInterest.Battery = true;
                    break
                case 'SR':
                    OpportunityInterest.Solar = true;
                    if (opportunityDetails.OpportunityInterest?.RRR) OpportunityInterest.RRR = true;
                    else OpportunityInterest.Roof = true;
                    break;
                case 'SBR':
                    OpportunityInterest.Solar = true;
                    OpportunityInterest.Battery = true;
                    OpportunityInterest.Roof = true;
                    break;
                case 'BR':
                    OpportunityInterest.Battery = true;
                    OpportunityInterest.Roof = true;
                    break;
                case 'RR':
                    OpportunityInterest.RR = true;
                    break;
                default:

            }

            /**
             * V1 SavePriceAndQuote posts a clone of RetrievePriceQuote (`quoteDetails`) with
             * package flags + finance overlays. Sparse V2 payloads omit CheckList / EPCCalc /
             * Design_Source / populated Roof and trigger API NullReferenceException.
             */
            const payload: any = JSON.parse(JSON.stringify(priceQuote || {}));
            payload.Solar = { ...(payload.Solar || {}), SolarPackage: false };
            payload.Battery = { ...(payload.Battery || {}), BatteryPackage: false };
            payload.RoofQuote = { ...(payload.RoofQuote || {}), RoofPackage: false };
            payload.BatteryOnlyPackage = { ...(payload.BatteryOnlyPackage || {}), BatteryOnlyPackage: false };
            payload.RoofOnlyPackage = { ...(payload.RoofOnlyPackage || {}), RoofOnlyPackage: false };
            payload.RR = { ...(payload.RR || {}), RRPackage: false, DesignId: payload.RR?.DesignId ?? 0 };
            payload.Roof = { ...(payload.Roof || {}) };
            payload.RoofOnly = payload.RoofOnly ?? null;
            payload.OpportunityInterest = OpportunityInterest;
            payload.QuoteVersion = null;
            payload.apr = [sunnovaAPIDetails?.APR || (roofDetails?.apr ? roofDetails.apr + '%' : '') || ''];
            payload.Cloned_Quote_Id = priceQuote?.Cloned_Quote_Id ?? 0;
            payload.RoofOnlyProductId = payload.RoofOnlyProductId || payload.RoofProductId || 0;
            payload.BatteryOnlyProductId = payload.BatteryOnlyProductId || payload.BatteryProductId || 0;
            payload.hoverEstimateID =
                selectedHoverEstimate?.hoverEstimateID ||
                priceQuote?.hoverEstimateID ||
                priceQuote?.Hover_Opportunity_Estimate_Id ||
                0;
            payload.RoofAdders = roofDetails?.RoofAdders || payload.RoofAdders || [];
            payload.BatteryAdders = payload.BatteryAdders ?? null;
            if (productType === 'R') {
                payload.Gross_Sales_Price_Per_Watt = null;
            }

            switch (productType) {
                case 'B':
                    payload.BatteryOnlyPackage.BatteryOnlyPackage = true;
                    break;
                case 'R':
                    payload.RoofOnlyPackage.RoofOnlyPackage = true;
                    break;
                case 'S':
                    payload.Solar.SolarPackage = true;
                    break;
                case 'SB':
                    payload.Solar.SolarPackage = true;
                    payload.Battery.BatteryPackage = true;
                    break;
                case 'SR':
                    payload.Solar.SolarPackage = true;
                    payload.RoofQuote.RoofPackage = true;
                    break;
                case 'SBR':
                    payload.Solar.SolarPackage = true;
                    payload.Battery.BatteryPackage = true;
                    payload.RoofQuote.RoofPackage = true;
                    break;
                case 'BR':
                    payload.Battery.BatteryPackage = true;
                    payload.RoofQuote.RoofPackage = true;
                    break;
                case 'RR':
                    payload.RR.RRPackage = true;
                    break;
                default:
                    break;
            }

            /* Hover Convert — prefer store overrides / selected estimate (Angular afterClose mapping). */
            if (selectedHoverEstimate?.hoverEstimateID) {
                payload.hoverEstimateID = selectedHoverEstimate.hoverEstimateID;
                payload.LowSlope = selectedHoverEstimate.lowSq;
                payload.NormalSlope = selectedHoverEstimate.normalSq;
                payload.Low_Slope_Override = selectedHoverEstimate.lowSq;
                payload.Normal_Slope_Override = selectedHoverEstimate.normalSq;
                payload.RoofSquareOverride =
                    (selectedHoverEstimate.lowSq || 0) + (selectedHoverEstimate.normalSq || 0);
                const shingleColorSF =
                    (selectedHoverEstimate.shingleTypeName
                        ? `${selectedHoverEstimate.shingleTypeName} - `
                        : '') + (selectedHoverEstimate.shingleType || '');
                const roofSlice = {
                    LowSlope: selectedHoverEstimate.lowSq,
                    NormalSlope: selectedHoverEstimate.normalSq,
                    RoofSquare: payload.RoofSquareOverride,
                    ShingleColor: selectedHoverEstimate.shingleType || null,
                    ShingleColorSF: shingleColorSF || null,
                    ManufacturerName: selectedHoverEstimate.shingleManufacturer || '',
                    RoofProduct: selectedHoverEstimate.shingleTypeName || '',
                };
                // V1 successful SavePriceAndQuote keeps Roof populated (empty Roof → API NRE).
                payload.Roof = { ...(payload.Roof || {}), ...roofSlice };
                if (OpportunityInterest.RoofOnly) {
                    payload.RoofOnly = { ...(payload.RoofOnly || {}), ...roofSlice };
                }
            } else if (priceQuoteOverrides) {
                if (priceQuoteOverrides.lowSlopeOverride != null) {
                    payload.LowSlope = priceQuoteOverrides.lowSlopeOverride;
                    payload.Low_Slope_Override = priceQuoteOverrides.lowSlopeOverride;
                }
                if (priceQuoteOverrides.normalSlopeOverride != null) {
                    payload.NormalSlope = priceQuoteOverrides.normalSlopeOverride;
                    payload.Normal_Slope_Override = priceQuoteOverrides.normalSlopeOverride;
                }
                if (priceQuoteOverrides.roofSquareOverride != null) {
                    payload.RoofSquareOverride = priceQuoteOverrides.roofSquareOverride;
                }
            }

            if (confirmQuoteCellKey?.product.includes("Solar") && (productType == 'S' || productType == 'SR' || productType == 'SB' || productType == 'SBR')) {
                let solar: any = {
                    IsDomestic: solarDetails?.SyncProductInfo?.IsDomestic,
                    SolarPackage: true,
                    FinancePartnerId: Number(solarDetails?.financePartnerId),
                    Total_Amount: (solarDetails?.Total_Amount) ? solarDetails?.Total_Amount : 0,
                    Deposit_Amount: (solarDetails?.Deposit_Amount) ? solarDetails?.Deposit_Amount : null,
                    ContractType: solarDetails?.contract_Type.trim() || '',
                    Finance_Method_Name: solarDetails?.contract_Type.trim() || '',
                    DesignId: Number((solarDetails?.design) ? solarDetails?.design : priceQuote.Solar.DesignId),
                    Tax: Number(solarDetails?.tax),
                    DownPayment: Number(solarDetails?.downPayment),
                    EPC: solarDetails?.epc || 0,
                    Escalator: (solarDetails?.Escalator) ? String(solarDetails?.Escalator) : '0',
                    PPW: solarDetails?.ppw || 0,
                    Rate: solarDetails?.SolarProductionRate ? Number(solarDetails.SolarProductionRate) : null,
                    Rebate: Number(solarDetails?.rebate),
                    TermYears: solarDetails?.financePartnerName == COMMON_CONSTANTS.EnfinPartnerName ? 25 : Number((sunnovaAPIDetails) ? sunnovaAPIDetails.TermLength : 0),
                    PricingMethod: (solarDetails?.pricingmethodName) ? solarDetails?.pricingmethodName.trim() : '',
                    PricingValue: (solarDetails?.financePartnerName?.toLocaleLowerCase() != 'cash')?Number(solarDetails?.pricingValue):0,
                    ITC_Amount: solarDetails?.ITC_Amount,
                    Monthly_Finance_Payment_NoITCPayback: solarDetails?.Monthly_Finance_Payment_NoITCPayback,
                    Rebate_Method: solarDetails?.rebateMethod || '',
                    Rebate_Amount_PerWatt: solarDetails?.RebateAmountPerWatt || 0,
                    Adder_Price: solarDetails?.AdderCost || 0,
                    Desired_PPW_Net: Number(solarDetails?.DesiredPPWNet),
                    Finance_Partner_Product_Id: solarDetails?.Finance_Partner_Product_Id || '',
                    IsACH: (solarDetails?.IsACH && solarDetails.IsACH.toLowerCase() === 'true') ? true : false,
                    Product_Type: solarDetails?.Product_Type || '',
                    Financed_Amount: (solarDetails?.Financed_Amount) ? solarDetails?.Financed_Amount : null,
                    AmountOnContractCompletion: (solarDetails?.UponCompletionOfTheContract) ? solarDetails?.UponCompletionOfTheContract : 0,
                    AmountOnPhysicalInstallCompletion: (solarDetails?.UponCompletionOfPhysicalInstall) ? solarDetails?.UponCompletionOfPhysicalInstall : 0,
                    AmountOnPhysicalInstallStart: (solarDetails?.UponCommencementOfPhysicalInstall) ? solarDetails?.UponCommencementOfPhysicalInstall : 0,
                    AmountOnWorkCompletion: undefined,
                    Finance_partner_contract_Type: sunnovaAPIDetails ? sunnovaAPIDetails.Name : '',
                    Offer_Id: solarDetails?.Offer_Id ?? '',
                    DealerFee: (sunnovaAPIDetails.DealerFee != 'null') ? Number(sunnovaAPIDetails.DealerFee) : 0
                }
                payload.Solar = solar;
                if(solarDetails){
                    payload.TotalSaleAmount = solarDetails.TotalSaleAmount;
                    payload.Dealer_Epc = solarDetails.dealerEpc;
                    payload.Dealer_Fee_Percentage = solarDetails.dealerFeePercentage;
                    payload.Gross_Sales_Price_Per_Watt = solarDetails.grossSalesPricePerWatt;
                    payload.PurchaseMethod = sunnovaAPIDetails.SfdcSyncValue;
                    payload.SfdcSyncValue = sunnovaAPIDetails.SfdcSyncValue;
                    payload.Production_rate = solarDetails.productionRate ? Number(solarDetails.productionRate) : 0;
                }
                
            }

            /**Set initial roof data  from retive api response */
            if (columnIncludesRoof(confirmQuoteCellKey?.product) && (OpportunityInterest.Roof || OpportunityInterest.RoofOnly || OpportunityInterest.RRR)) {

                // Merge into RPQ RoofQuote — do not replace with a sparse object (drops Finance_Method_Name etc.).
                // V1 Cash RoofQuote has neither Offer_Id nor Finance_Partner_Product_Id — do not invent "".
                const nextRoofQuote: Record<string, any> = {
                    ...(payload.RoofQuote || {}),
                    RoofPackage: payload.RoofQuote?.RoofPackage ?? false,
                    FinancePartnerId: payload.RoofQuote?.FinancePartnerId ?? priceQuote.RoofQuote?.FinancePartnerId,
                    ContractType: payload.RoofQuote?.ContractType ?? priceQuote.RoofQuote?.ContractType ?? null,
                    TermYears: payload.RoofQuote?.TermYears ?? priceQuote.RoofQuote?.TermYears ?? null,
                    TermAPR: payload.RoofQuote?.TermAPR ?? priceQuote.RoofQuote?.TermAPR ?? null,
                    DownPayment: payload.RoofQuote?.DownPayment ?? priceQuote.RoofQuote?.DownPayment ?? null,
                    Normal_Slope_Price_Override: payload.RoofQuote?.Normal_Slope_Price_Override ?? priceQuote.RoofQuote?.Normal_Slope_Price_Override ?? null,
                    Low_Slope_Price_Override: payload.RoofQuote?.Low_Slope_Price_Override ?? priceQuote.RoofQuote?.Low_Slope_Price_Override ?? null,
                    Pricing_Override_Type: payload.RoofQuote?.Pricing_Override_Type ?? priceQuote.RoofQuote?.Pricing_Override_Type ?? null,
                    Total_Roofing_Override: payload.RoofQuote?.Total_Roofing_Override ?? priceQuote.RoofQuote?.Total_Roofing_Override ?? null,
                    Deposit_Amount: payload.RoofQuote?.Deposit_Amount ?? priceQuote.RoofQuote?.Deposit_Amount ?? null,
                    Financed_Amount: payload.RoofQuote?.Financed_Amount ?? priceQuote.RoofQuote?.Financed_Amount ?? null,
                    AmountOnWorkCompletion: payload.RoofQuote?.AmountOnWorkCompletion ?? priceQuote.RoofQuote?.AmountOnWorkCompletion ?? 0,
                    DesignId: payload.RoofQuote?.DesignId ?? priceQuote.RoofQuote?.DesignId ?? 0,
                };
                const liveRoofDetails = useOneButtonStore.getState().roofDetails ?? roofDetails;
                const roofQuoteOfferId =
                    resolveRoofOfferIdFromDetails(liveRoofDetails) ||
                    payload.RoofQuote?.Offer_Id ||
                    priceQuote.RoofQuote?.Offer_Id ||
                    null;
                const roofQuotePartnerProductId =
                    resolveRoofOfferIdFromDetails(liveRoofDetails) ||
                    (payload.RoofQuote?.Finance_Partner_Product_Id ??
                        priceQuote.RoofQuote?.Finance_Partner_Product_Id ??
                        null);
                if (roofQuoteOfferId) nextRoofQuote.Offer_Id = roofQuoteOfferId;
                else delete nextRoofQuote.Offer_Id;
                if (roofQuotePartnerProductId) nextRoofQuote.Finance_Partner_Product_Id = roofQuotePartnerProductId;
                else delete nextRoofQuote.Finance_Partner_Product_Id;
                payload.RoofQuote = nextRoofQuote;

                // Solar + Roof (SR) or Battery + Roof (BR): roof line from modal; multi-contract when roof contract ≠ primary line (solar or battery).
                if ((productType === 'SR' || productType === 'BR') && OpportunityInterest?.Roof) {
                    payload.RoofQuote.RoofPackage = true;

                    const primaryContractType = productType === 'SR'
                        ? String((payload.Solar as SolarObject)?.ContractType || '').trim()
                        : String(batteryDetails?.financeMethodName || '').trim();
                    const roofContractType = roofDetails?.contract_Type?.trim() || '';

                    if (roofContractType !== '' && primaryContractType !== '' && roofContractType !== primaryContractType) {
                        quoteIsMultiContract = true;
                    }

                    if (!quoteIsMultiContract) {
                        payload.RoofQuote.FinancePartnerId = roofDetails?.financePartnerId;
                        payload.RoofQuote.ContractType = roofContractType;
                        payload.RoofQuote.TermYears = String(roofDetails?.term || null);
                        payload.RoofQuote.TermAPR = String(roofDetails?.apr || '');
                        payload.RoofQuote.DownPayment = roofDetails?.CustomerDeposit;
                        payload.TotalSaleAmount = Number(roofDetails?.gross_Sales_Price || 0);
                    } else {
                        payload.RoofOnlyPackage.FinancePartnerId = roofDetails?.financePartnerId;
                        payload.RoofOnlyPackage.ContractType = roofContractType;
                        payload.RoofOnlyPackage.TermYears = String(roofDetails?.term || null);
                        payload.RoofOnlyPackage.TermAPR = String(roofDetails?.apr || '');
                        payload.RoofOnlyPackage.DownPayment = roofDetails?.CustomerDeposit;
                    }
                    payload.RoofAdders = roofDetails?.RoofAdders || [];
                    payload.TotalCommissionableAmount = Number(roofDetails?.sales_Commission);
                    //  payload.TotalRoof = roofDetails?.Roofsq;
                    payload.RoofCostType = roofDetails?.RoofCostType;
                    payload.RoofCommissionTier = roofDetails?.commission_Tier;
                    payload.RoofFinancingPartner = roofDetails?.financePartnerName;
                    payload.RoofFinanceMethod = roofDetails?.contract_Type.trim()
                    payload.RoofFinanceTerm = (roofDetails?.financePartnerName != 'Cash') ? String(roofDetails?.term) : roofDetails?.contract_Type.trim();
                    payload.PurchaseMethod = roofDetails?.purchaseMethod;
                    payload.RoofQuote.Normal_Slope_Price_Override = (roofDetails?.roofForm.roofCostDetails.Normal_Slope_Price_Override != '' ? roofDetails?.roofForm.roofCostDetails.Normal_Slope_Price_Override : null);
                    payload.RoofQuote.Low_Slope_Price_Override = (roofDetails?.roofForm.roofCostDetails.Low_Slope_Price_Override != '' ? roofDetails?.roofForm.roofCostDetails.Low_Slope_Price_Override : null);
                    payload.RoofQuote.Pricing_Override_Type = (roofDetails?.roofForm.roofCostDetails.Pricing_Override_Type != '' ? roofDetails?.roofForm.roofCostDetails.Pricing_Override_Type : null);
                    payload.RoofQuote.Total_Roofing_Override = (roofDetails?.roofForm.roofCostDetails.Total_Roofing_Override != '' ? roofDetails?.roofForm.roofCostDetails.Total_Roofing_Override : null);
                    payload.RoofQuote.Offer_Id = roofDetails?.Offer_Id;
                    payload.RoofQuote.Finance_Partner_Product_Id =
                        roofDetails?.financePartnerProductId ??
                        roofDetails?.Finance_Partner_Product_Id ??
                        roofDetails?.Offer_Id;
                    payload.RoofQuote.Deposit_Amount = (roofDetails?.Deposit_Amount) ? roofDetails?.Deposit_Amount : null;
                    payload.RoofQuote.Financed_Amount =
                        roofDetails?.Financed_Amount != null && roofDetails?.Financed_Amount !== ''
                            ? Number(roofDetails.Financed_Amount)
                            : null;
                    payload.RoofQuote.Finance_Method_Name = roofDetails?.contract_Type?.trim() || null;
                }

                let qgId = effectiveQuoteGroupIdForSrPack(productType, OpportunityInterest, quoteGroupId);
                if (productType == 'SR' && OpportunityInterest.RRR) productType = 'SRRR';
                else if (productType == 'R' && OpportunityInterest.RRR) productType = 'RRR';
                else if (productType == 'BR' && OpportunityInterest.RRR) productType = 'BRRR';
                else if (productType == 'SBR' && OpportunityInterest.RRR) productType = 'SBRRR';
                else if (productType == 'RR' && OpportunityInterest.RR) productType = 'RR';

                const roofOnlyObj: Record<string, any> = {
                    ...(payload.RoofOnlyPackage || {}),
                    RoofOnlyPackage: true,
                    ContractType: roofDetails?.contract_Type?.trim() || payload.RoofOnlyPackage?.ContractType || priceQuote.RoofOnlyPackage?.ContractType || '',
                    Tax: payload.RoofOnlyPackage?.Tax ?? priceQuote.RoofOnlyPackage?.Tax ?? null,
                    // V2: numeric DownPayment (API accepts number; V1 used string "0")
                    DownPayment: Number(roofDetails?.CustomerDeposit ?? payload.RoofOnlyPackage?.DownPayment ?? 0),
                    EPC: payload.RoofOnlyPackage?.EPC ?? priceQuote.RoofOnlyPackage?.EPC ?? null,
                    Escalator: payload.RoofOnlyPackage?.Escalator ?? priceQuote.RoofOnlyPackage?.Escalator ?? null,
                    PPW: payload.RoofOnlyPackage?.PPW ?? priceQuote.RoofOnlyPackage?.PPW ?? null,
                    Rate: payload.RoofOnlyPackage?.Rate ?? priceQuote.RoofOnlyPackage?.Rate ?? null,
                    Rebate: payload.RoofOnlyPackage?.Rebate ?? priceQuote.RoofOnlyPackage?.Rebate ?? null,
                    TermYears: String(roofDetails?.term ?? payload.RoofOnlyPackage?.TermYears ?? 0),
                    PricingMethod: payload.RoofOnlyPackage?.PricingMethod ?? priceQuote.RoofOnlyPackage?.PricingMethod ?? null,
                    PricingValue: payload.RoofOnlyPackage?.PricingValue ?? priceQuote.RoofOnlyPackage?.PricingValue ?? null,
                    PermitPayer: payload.RoofOnlyPackage?.PermitPayer ?? priceQuote.RoofOnlyPackage?.PermitPayer ?? null,
                    TermAPR: String(roofDetails?.apr ?? payload.RoofOnlyPackage?.TermAPR ?? 0),
                    Finance_Partner_Product_Id: payload.RoofOnlyPackage?.Finance_Partner_Product_Id ?? priceQuote.RoofOnlyPackage?.Finance_Partner_Product_Id ?? null,
                    DesignId: payload.RoofOnlyPackage?.DesignId ?? priceQuote.RoofOnlyPackage?.DesignId ?? 0,
                    FinancePartnerId: roofDetails?.financePartnerId ?? payload.RoofOnlyPackage?.FinancePartnerId ?? priceQuote.RoofOnlyPackage?.FinancePartnerId,
                    FinanceMethodId: payload.RoofOnlyPackage?.FinanceMethodId ?? 0,
                    DealerFee: payload.RoofOnlyPackage?.DealerFee ?? 0,
                    QuoteId: payload.RoofOnlyPackage?.QuoteId ?? 0,
                    Finance_Method_Name: payload.RoofOnlyPackage?.Finance_Method_Name ?? null,
                };
                payload.RoofOnlyPackage = roofOnlyObj;

            }

            /**set payload from roof details */
            if ((OpportunityInterest.RoofOnly || OpportunityInterest.Roof || OpportunityInterest.RRR) && (productType == 'R' || productType == 'RRR')) {
                payload.RoofOnlyPackage.FinancePartnerId =
                    roofDetails?.financePartnerId ?? payload.RoofOnlyPackage.FinancePartnerId;
                payload.RoofOnlyPackage.ContractType = roofDetails?.contract_Type?.trim();
                payload.RoofOnlyPackage.TermYears = String(roofDetails?.term || 0);
                payload.RoofOnlyPackage.TermAPR = String(roofDetails?.apr || 0);
                // V2: numeric DownPayment (CustomerDeposit); Deposit_Amount holds cash deposit.
                payload.RoofOnlyPackage.DownPayment = Number(roofDetails?.CustomerDeposit ?? 0);
                payload.RoofAdders = (roofDetails?.RoofAdders || []).map((a: any) => ({
                    ...a,
                    Commissonable: a.Commissonable || 'N',
                    RR_Price: a.RR_Price ?? null,
                    AdderCost: a.AdderCost,
                }));
                payload.TotalCommissionableAmount = Number(roofDetails?.sales_Commission || 0);
                payload.TotalSaleAmount = Number(roofDetails?.gross_Sales_Price || 0);
                payload.TotalRoof = roofDetails?.Roofsq ?? payload.RoofSquareOverride ?? null;
                payload.RoofCostType = roofDetails?.RoofCostType;
                payload.RoofCommissionTier = roofDetails?.commission_Tier ?? '';
                payload.RoofFinancingPartner = roofDetails?.financePartnerName;
                payload.RoofFinanceMethod = roofDetails?.contract_Type?.trim();
                payload.RoofFinanceTerm = (roofDetails?.financePartnerName !== 'Cash') ? String(roofDetails?.term || '') : roofDetails?.contract_Type?.trim();
                payload.PurchaseMethod = roofDetails?.purchaseMethod || payload.PurchaseMethod;
                payload.SfdcSyncValue = sunnovaAPIDetails?.SfdcSyncValue || payload.SfdcSyncValue || '';
                payload.RoofQuote.Normal_Slope_Price_Override = roofDetails?.normalSlopePriceOverride != null && roofDetails?.normalSlopePriceOverride !== ''
                    ? Number(roofDetails.normalSlopePriceOverride)
                    : null;
                payload.RoofQuote.Low_Slope_Price_Override = roofDetails?.lowSlopePriceOverride != null && roofDetails?.lowSlopePriceOverride !== ''
                    ? Number(roofDetails.lowSlopePriceOverride)
                    : null;
                payload.RoofQuote.Pricing_Override_Type = roofDetails?.Pricing_Override_Type || null;
                payload.RoofQuote.Total_Roofing_Override = roofDetails?.Total_Roofing_Override || null;
                payload.RoofQuote.Finance_Method_Name = roofDetails?.contract_Type?.trim() || null;
                payload.RoofQuote.FinancePartnerId = roofDetails?.financePartnerId ?? payload.RoofQuote.FinancePartnerId;
                const depositAmt = roofDetails?.Deposit_Amount;
                payload.RoofQuote.Deposit_Amount =
                    depositAmt != null && depositAmt !== '' ? Number(depositAmt) : null;
                payload.RoofQuote.Financed_Amount =
                    roofDetails?.Financed_Amount != null && roofDetails?.Financed_Amount !== ''
                        ? Number(roofDetails.Financed_Amount)
                        : null;
                payload.RoofQuote.AmountOnWorkCompletion = Number(roofDetails?.PlusAddersUponCompletionOfWork || 0);
                payload.Dealer_Fee_Percentage = String(roofDetails?.Dealer_Fee_Percentage ?? '0');
                payload.Dealer_Epc = String(roofDetails?.Dealer_Epc ?? payload.Dealer_Epc ?? '0');
                payload.Monthly_Finance_Partner_Payment = Number(roofDetails?.Monthly_Finance_Partner_Payment || 0);
                // V1 working payload keeps shingle/square data on Roof; RoofOnly is null.
                if (OpportunityInterest.RoofOnly && payload.Roof?.RoofSquare) {
                    payload.RoofOnly = null;
                }
                if(OpportunityInterest.RRR) payload.RR_Price = roofDetails?.RoofAdders[0]?.RR_Price ? Number(roofDetails?.RoofAdders[0]?.RR_Price) : null;
            }

            if (confirmQuoteCellKey?.product.includes("Battery") && (productType == 'B' || productType === 'BR')) {
                // Standalone B: headline totals from battery. Combined BR: roof block above sets headline totals; only attach battery package + adders.
                if (productType === 'B') {
                    payload.PriceTier = batteryDetails?.priceTier || null;
                    payload.TotalSaleAmount = (batteryDetails?.grossSalesPrice || 0);
                    payload.TotalCommissionableAmount = batteryDetails?.totalCommissionableAmount || 0;
                    payload.PurchaseMethod = (batteryDetails?.purchaseMethod || '');
                    payload.RoofCommissionTier = roofDetails?.commission_Tier;
                    payload.Dealer_Fee_Percentage = batteryDetails?.dealerFee || 0;
                    payload.Dealer_Epc =  solarDetails?.dealerEpc ?? (priceQuote.Dealer_Epc || 0);
                    payload.Monthly_Finance_Partner_Payment = isLightreachBatteryLeaseQuote()
                        ? resolveLrBoMonthlyForSave()
                        : batteryDetails?.monthlyPayment || 0;
                }

                payload.BatteryOnlyPackage = {
                    BatteryOnlyPackage: true,
                    FinancePartnerId: Number(batteryDetails?.financePartnerId || 0),
                    ContractType: (batteryDetails?.financeMethodName || '').trim(),
                    TermYears: String(batteryDetails?.term) || null,
                    TermAPR: batteryTermAprOrNull(batteryDetails?.apr),
                    Escalator: (batteryDetails?.Escalator) ? String(batteryDetails.Escalator) : '0',
                    DownPayment: Number(batteryDetails?.downPayment || 0),
                    Rebate: Number(batteryDetails?.rebate || 0),
                    Tax: Number(batteryDetails?.tax || 0),
                    PricingMethod: (batteryDetails?.pricingMethod || '').trim(),
                    PricingValue: Number(batteryDetails?.price || 0),
                    Rate: Number(batteryDetails?.rate || 0),
                    Offer_Id: lrBoOfferIdForSave(),
                    Finance_Partner_Product_Id: batteryDetails?.Finance_Partner_Product_Id || '',
                    Deposit_Amount: batteryDetails?.depositAmount || null,
                    Financed_Amount: batteryDetails?.financedAmount || 0,
                    AmountOnPhysicalInstallCompletion: batteryDetails?.uponCompletionOfPhysicalInstall || 0,
                    AmountOnWorkCompletion: batteryDetails?.uponCompletionOfWork || 0,
                    Total_Amount: batteryDetails?.grossSalesPrice || 0,
                }

                payload.BatteryAdders = mapBatteryAddersForSave(batteryDetails);
                payload.batteryAdders = payload.BatteryAdders;
            }
            if (confirmQuoteCellKey?.product.includes("Battery") && (productType == 'SB')){
                payload.Battery = {
                    BatteryPackage: true,
                    FinancePartnerId: Number(batteryDetails?.financePartnerId || 0),
                    ContractType: (batteryDetails?.financeMethodName || '').trim(),
                    TermYears: String(batteryDetails?.term) || null,
                    TermAPR: String(batteryDetails?.apr) || null,
                    DownPayment: Number(batteryDetails?.downPayment || 0),
                    Rebate: Number(batteryDetails?.rebate || 0),
                    Tax: Number(batteryDetails?.tax || 0),
                    PricingMethod: (batteryDetails?.pricingMethod || '').trim(),
                    PricingValue: Number(batteryDetails?.price || 0),
                    Rate: Number(batteryDetails?.rate || 0),
                    Offer_Id: batteryDetails?.Offer_Id || '',
                    Finance_Partner_Product_Id: batteryDetails?.Finance_Partner_Product_Id || '',
                    Deposit_Amount: batteryDetails?.depositAmount || null,
                    Financed_Amount: batteryDetails?.financedAmount || 0,
                    AmountOnPhysicalInstallCompletion: batteryDetails?.uponCompletionOfPhysicalInstall || 0,
                    AmountOnWorkCompletion: batteryDetails?.uponCompletionOfWork || 0,
                    Total_Amount: batteryDetails?.grossSalesPrice || 0,
                }
                if(batteryDetails?.grossSalesPrice) payload.TotalSaleAmount = (batteryDetails?.grossSalesPrice);
                payload.TotalCommissionableAmount = Number(batteryDetails?.totalCommissionableAmount);
                payload.BatteryAdders = mapBatteryAddersForSave(batteryDetails);
                payload.batteryAdders = payload.BatteryAdders;
            }

            if (confirmQuoteCellKey?.product.includes("Battery")) {
                const batteryProductTypeId = resolveProductTypeIdFromCategory(
                    COMMON_CONSTANTS.BatteryProductName,
                    productTypeList,
                    opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [],
                );
                payload.FinancePartnerQuoteAdders = [
                    ...(Array.isArray(payload.FinancePartnerQuoteAdders) ? payload.FinancePartnerQuoteAdders : []),
                    ...financePartnerQuoteAddersFromBattery(batteryDetails, batteryProductTypeId),
                ];
            }

            if (OpportunityInterest.RR && productType === 'RR') {
                const liveRR = useOneButtonStore.getState().rrDetails ?? rrDetails;
                payload.RR = {
                    ...(payload.RR || {}),
                    RRPackage: true,
                    FinancePartnerId: liveRR?.financePartnerId ?? payload.RR?.FinancePartnerId ?? null,
                    ContractType: liveRR?.contract_Type ?? null,
                    Finance_Method_Name: liveRR?.Finance_Method_Name ?? liveRR?.contract_Type ?? null,
                    PricingMethod: liveRR?.PricingMethod ?? null,
                    PricingValue: liveRR?.PricingValue ?? null,
                    DownPayment: liveRR?.DownPayment ?? 0,
                    Deposit_Amount: liveRR?.Deposit_Amount != null && liveRR?.Deposit_Amount !== ''
                        ? Number(liveRR.Deposit_Amount)
                        : null,
                    Financed_Amount: liveRR?.Financed_Amount != null && liveRR?.Financed_Amount !== ''
                        ? Number(liveRR.Financed_Amount)
                        : null,
                    DesignId: payload.RR?.DesignId ?? 0,
                };
                payload.RR_System_Size = liveRR?.RR_System_Size ? liveRR.RR_System_Size : '0';
                payload.TotalSaleAmount = Number(liveRR?.TotalSaleAmount || 0);
                payload.RoofQuote = {
                    ...(payload.RoofQuote || {}),
                    Deposit_Amount: liveRR?.Deposit_Amount != null && liveRR?.Deposit_Amount !== ''
                        ? Number(liveRR.Deposit_Amount)
                        : null,
                };
            }

            if (OpportunityInterest.Solar && solarDetails) {
                // payload.Monthly_Finance_Payment_NoITCPayback = Number(solarDetails?.Monthly_Finance_Payment_NoITCPayback);
                payload.Monthly_Finance_Partner_Payment = Number(solarDetails?.Monthly_Finance_Partner_Payment);
              } else if (OpportunityInterest.BatteryOnly)  {
                if (isLightreachBatteryLeaseQuote()) {
                  const monthly = resolveLrBoMonthlyForSave();
                  if (monthly != null) payload.Monthly_Finance_Partner_Payment = monthly;
                } else {
                  payload.Monthly_Finance_Partner_Payment = Number(
                    (batteryDetails?.monthlyPayment ?? batteryDetails?.Monthly_Finance_Partner_Payment) || 0,
                  );
                }
              }

            let qgId = effectiveQuoteGroupIdForSrPack(productType, OpportunityInterest, quoteGroupId);
            if (productType == 'SR' && OpportunityInterest.RRR) productType = 'SRRR';
            else if (productType == 'R' && OpportunityInterest.RRR) productType = 'RRR';
            else if (productType == 'BR' && OpportunityInterest.RRR) productType = 'BRRR';
            else if (productType == 'SBR' && OpportunityInterest.RRR) productType = 'SBRRR';
            else if (productType == 'RR' && OpportunityInterest.RR) productType = 'RR';

            if(confirmQuoteCellKey?.clonedQuoteDetails) {
                payload.Cloned_Quote_Id = confirmQuoteCellKey.clonedQuoteDetails.QuoteId;
            }

            applyGoodLeapRoofOfferFields(payload, useOneButtonStore.getState().roofDetails ?? roofDetails);

            savePriceAndQuote(payload, productType, qgId).then((response: any) => {
                // setFooterMessage({
                //     type: 'success',
                //     text: `${getProductName(productType)} quote created successfully`,
                // });

                noticeApi.success({
                    message: 'Quote Created',
                    description: `${getProductName(productType)} quote created successfully`,
                    className: notifClass,
                });
                resolve(response);
            }).catch((err) => {
                const msg = err?.response?.data?.Message
                    ?? err?.error?.Message
                    ?? err?.Message
                    ?? `Failed to generate ${getProductName(productType)} quote. Please try again.`;
                setFooterMessage(
                    toFooterMessage('error', typeof msg === 'string' ? msg : String(msg)),
                );
                reject(err);
            });
        });

        // }
    }

    // Step 4
    async function savePriceAndQuote(obj: any, productTypeName: string, quoteGroupIdParam?: string | number | null) {
        // V1 always sends quoteGroupId on the query string (e.g. quoteGroupId=5&product_category=R).
        updateLoaderCount(true);
        try {
            const res: any = await quoteServices.savePriceAndQuote(obj, productTypeName, quoteGroupIdParam);
            const data = {
                quoteId: Number(res.Message),
                opportunity_Id: priceQuote.opportunity_Id,
                salesforceID: priceQuote.SalesForce_Id
            };

            // Angular: `SolarProductName` || `SolarProductNotation` (notation is `S`; combined codes like `SB` skip priceCheck).
            if (
                productTypeName === COMMON_CONSTANTS.SolarProductName
                || productTypeName === 'S'
            ) {
                try {
                    await quoteServices.priceCheck(data);
                } catch (_) {
                    /* Angular resolves promise even when priceCheck errors */
                }
            }

            const financePartnerName = checkProductTypeFinancePartner(obj, productTypeName, opportunityDetails);
            const oppId = priceQuote.Opportunity_Id;

            if (financePartnerName === COMMON_CONSTANTS.GoodLeapPartnerName) {
                try {
                    await quoteServices.contractUpdateLoanStatus(oppId, 'Quote', Number(res.Message));
                    await quoteServices.contractGetLoanStatusByOpportunity(oppId);
                } catch (_) {
                    /* Angular: nested subscribe — no user toast on failure */
                }
            }

            const hasLightReachfinancePartner =
                financePartnerName === COMMON_CONSTANTS.LightreachPartnerName;
            if (hasLightReachfinancePartner) {
                try {
                    const changeOrderDetails = await quoteServices.contractGetLoanStatusByOpportunity(oppId);
                    const contractStatus = Array.isArray(changeOrderDetails)
                        ? changeOrderDetails.find(
                            (x: any) =>
                                x.Finance_Partner_Name === COMMON_CONSTANTS.LightreachPartnerName
                        )
                        : undefined;
                    const appStatus = contractStatus?.application_Status;
                    if (
                        appStatus === 'Signed'
                        || appStatus === 'Sent'
                        || appStatus === 'Generated'
                    ) {
                        await quoteServices.contractUpdateLoanStatus(oppId, 'Quote', 0);
                        await quoteServices.contractGetLoanStatusByOpportunity(oppId);
                        const financeMethod =
                            obj?.Solar?.Finance_Method_Name ?? obj?.Solar?.ContractType ?? '';
                        let changeOrderMessage =
                            `Quote created: Finance method - ${financeMethod},Escalator - ${obj?.Solar?.Escalator}%,Pricing - ${obj?.Solar?.PricingMethod},Solar rate ${obj?.Solar?.PricingValue}`;
                        const battQty =
                            obj?.BatteryInformation?.Quantity ?? priceQuote?.BatteryInformation?.Quantity;
                        if (battQty) {
                            changeOrderMessage += `,Battery count - ${battQty}.`;
                        }
                        await quoteServices.savePageChanges({
                            opportunityId: oppId,
                            pageName: 'Quote',
                            changes: changeOrderMessage,
                        });
                        await quoteServices.getPageChanges(oppId);
                    }
                } catch (_) {
                    /* Angular: no toast from this branch */
                }
            }

            return res;
        } catch (err) {
            console.error('Error in savePriceAndQuote:', err);
            throw err;
        } finally {
            updateLoaderCount(false);
        }
    }

    // Step 5

    const postSavePriceAndQuoteOperations = (quoteId?: string) => {
        setLoaderCount(0);
        setIsGenerating(false);
        setShowLoadingOverlay(false);
        updateQuoteVersion(quoteId);
    };

    const getProductName = (productType: string) => {
        let productName = '';
        switch (productType) {
            case "S":
                productName = "Solar";
                break;
            case "B":
                productName = "Battery";
                break;
            case "R":
                productName = "Roof";
                break;
            case "RRR":
                productName = "RRR";
                break;
            case "SR":
                productName = "Solar + Roof";
                break;
            case "SRRR":
                productName = "Solar + RRR";
                break;
            case "SB":
                productName = "Solar + Battery";
                break;
            case "BR":
                productName = "Battery + Roof";
                break;
            case "BRRR":
                productName = "Battery + RRR";
                break;
            case "SBR":
                productName = "Solar + Battery + Roof";
                break;
            case "SBRRR":
                productName = "Solar + Battery + RRR";
                break;
            case "RR":
                productName = "RR";
                break;
        }

        return productName;
    }


    const updateLoaderCount = React.useCallback((loader: boolean) => {

        setLoaderCount((prev) => {
            let loaders = prev < 0 ? 0 : prev;
            if (loader) {
                loaders++;
            } else {
                loaders = loaders > 0 ? loaders - 1 : 0;
            }
            return loaders;
        });
    }, []);

    const setErrorMessage = (message: string) => {
        if (loaderCount > 0) return;
        if (message?.trim()) {
            setFooterMessage(toFooterMessage('error', message));
            return;
        }
        setFooterMessage(null);
    }

    async function GetOneDrawDesignReadiness() {
        return new Promise<any>((resolve, reject) => {
            updateLoaderCount(true);
            quoteServices.GetOneDrawDesignReadiness(priceQuote.Opportunity_Id).then((res: any) => {
                updateLoaderCount(false);
                resolve(res);
            }, err => {
                updateLoaderCount(false);
                resolve(false);
            })
        })

    }
    const showMultiHint =
        confirmQuoteCellKey &&
        (columnIncludesSolar(confirmQuoteCellKey.product) ? 1 : 0) +
        (columnIncludesRoof(confirmQuoteCellKey.product) ? 1 : 0) +
        (columnIncludesBattery(confirmQuoteCellKey.product) ? 1 : 0) >
        1;

    const getConfirmQuoteCellKey = (prduct: string) => {
        let tab:any = tabs.find((x: any) => x.key === prduct);
        tab.quote.clonedQuoteDetails = confirmQuoteCellKey?.clonedQuoteDetails;
        tab.quote.productType = setProductType(prduct);
        tab.quote.tier = confirmQuoteCellKey?.tier ?? tab.quote.tier;
        tab.quote.ExcelOffer = confirmQuoteCellKey?.ExcelOffer ?? tab.quote.ExcelOffer;
        tab.quote.payload = confirmQuoteCellKey?.payload ?? tab.quote.payload;
        tab.quote.Recommended = confirmQuoteCellKey?.Recommended ?? tab.quote.Recommended;
        return tab?.quote 
    }

    const setProductType = (product:string):string => {
        let productType:string = '';
        if (priceQuote.Solar?.SolarPackage) { productType = 'S'; }
        if (priceQuote.Battery?.BatteryPackage) productType = productType + 'B';
        if (priceQuote.BatteryOnlyPackage?.BatteryOnlyPackage) productType = 'B';
        if (priceQuote.RoofQuote?.RoofPackage) productType = productType + 'R';
        if (priceQuote.RoofOnlyPackage?.RoofOnlyPackage) productType = 'R';
        if (priceQuote.RR?.RRPackage) productType = 'RR';
        const selectedProduct = (product == 'solar')?'S':(product == 'battery')?'B':'R';
        return getProductType(selectedProduct, productType)
      }

      const getProductType = (product: String, currentProductType?: any):string =>{
        let productType: String = '';
        switch (currentProductType) {
          case 'SB':
            if (priceQuote.Solar.FinancePartnerId == priceQuote.Battery.FinancePartnerId)
              productType = 'SB';
            else productType = product;
            break;
          case 'SR':
            if (priceQuote.Solar.FinancePartnerId == priceQuote.RoofQuote.FinancePartnerId)
              productType = 'SR';
            else productType = product;
            break;
          case 'SBR':
            if (priceQuote.Solar.FinancePartnerId == priceQuote.Battery.FinancePartnerId
              && priceQuote.Solar.FinancePartnerId == priceQuote.RoofQuote.FinancePartnerId)
              productType = 'SBR';
            else if (priceQuote.Solar.FinancePartnerId == priceQuote.Battery.FinancePartnerId && (product == 'S' || product == 'B')) productType = 'SB';
            else if (priceQuote.Solar.FinancePartnerId == priceQuote.RoofQuote.FinancePartnerId && (product == 'S' || product == 'R')) productType = 'SR';
            else if (priceQuote.Battery.FinancePartnerId == priceQuote.RoofQuote.FinancePartnerId && (product == 'B' || product == 'R')) productType = 'BR';
            else productType = product;
            break;
          default:
            productType = currentProductType;
        }
        return productType;
      }

    const isLightreachBatteryLeaseQuote = () => {
        const battery = batteryDetailsRef.current ?? batteryDetails;
        const partner = String(
            battery?.financePartnerName || confirmQuoteCellKey?.partner || '',
        )
            .replace(/\s+/g, '')
            .toLowerCase();
        const method = String(
            battery?.financeMethodName ||
                confirmQuoteCellKey?.methord ||
                priceQuote?.BatteryOnlyPackage?.Finance_Method_Name ||
                priceQuote?.BatteryOnlyPackage?.ContractType ||
                priceQuote?.Battery?.Finance_Method_Name ||
                '',
        );
        return partner.includes('lightreach') && method.toLowerCase().includes('battery lease');
    };

    const resolveLrBoMonthlyForSave = () => {
        const battery = batteryDetailsRef.current ?? batteryDetails;
        const fromDetails = Number(battery?.monthlyPayment ?? battery?.Monthly_Finance_Partner_Payment);
        const excel = confirmQuoteCellKey?.ExcelOffer ?? confirmQuoteCellKey?.payload?.ExcelOffer;
        const planTotal =
            Number(excel?.batteryEPC ?? excel?.BatteryEPC ?? 0) + Number(excel?.batteryAdder ?? excel?.BatteryAdder ?? 0);
        const gross = Number(battery?.grossSalesPrice);
        const amountChanged = planTotal > 0 && Number.isFinite(gross) && Math.abs(gross - planTotal) > 1;
        if (amountChanged && Number.isFinite(fromDetails) && fromDetails > 0) return fromDetails;
        const fromOffer =
            year1TotalMonthlyFromLrBoOffer(confirmQuoteCellKey?.payload) ??
            year1TotalMonthlyFromLrBoOffer(confirmQuoteCellKey?.ExcelOffer) ??
            year1TotalMonthlyFromLrBoOffer(confirmQuoteCellKey);
        if (Number.isFinite(fromDetails) && fromDetails > 0) return fromDetails;
        return fromOffer;
    };

    const lrBoOfferIdForSave = () => {
        const battery = batteryDetailsRef.current ?? batteryDetails;
        return (
            battery?.Offer_Id ||
            productIdFromLrBoOffer(confirmQuoteCellKey?.payload) ||
            productIdFromLrBoOffer(confirmQuoteCellKey?.ExcelOffer) ||
            ''
        );
    };

    const getTabContent = (tab: { key: string; kind: string; label: string; quote: selectedQuote }) => {
        switch (tab.kind) {
            case 'solar':
                return (
                    <SolarQuoteDetails
                        confirmQuoteCellKey={getConfirmQuoteCellKey('solar') as selectedQuote}
                        priceQuote={effectivePriceQuote}
                        theme={theme}
                        lineMode="solar"
                        lightreachEligibleProductData={lightreachEligibleProductData}
                        lightreachEligibleProductsReady={!isLightReach || lightreachPriceCapsReady}
                        lightreachPriceCapsData={lightreachPriceCapsData}
                        batteryTotalAmount={Number(batteryDetails?.grossSalesPrice) || 0}
                        updateLoaderCount={updateLoaderCount}
                        updateSolarDetails={(data: any) => { setSolarDetails(data) }}
                        updateErrorMessage={(message: string) => { setErrorMessage(message) }}
                    />
                );
            case 'battery':
                return (
                    <BatteryQuoteDetails
                        confirmQuoteCellKey={getConfirmQuoteCellKey('battery') as selectedQuote}
                        priceQuote={priceQuote}
                        theme={theme}
                        updateLoaderCount={updateLoaderCount}
                        lightreachPriceCapsData={lightreachPriceCapsData}
                        lightreachPriceCapsReady={lightreachPriceCapsReady}
                        lightreachBatteryLeaseLeadId={lightreachBatteryLeaseLeadId}
                        updateErrorMessage={(message: string) => { setErrorMessage(message) }}
                        updateBatteryDetails={applyBatteryDetails}
                        solarMasterSync={batterySolarMasterSync}
                    />
                );
            case 'roof':
                return (
                    <RoofQuoteDetails
                        key={`roof-hover-${selectedHoverEstimate?.hoverEstimateID || 'none'}`}
                        confirmQuoteCellKey={tab.quote as selectedQuote}
                        priceQuote={priceQuote}
                        theme={theme}
                        updateLoaderCount={updateLoaderCount}
                        solarMasterSync={roofSolarMasterSync}
                    />
                );
            case 'rr':
                return (
                    <RRQuoteDetails
                        confirmQuoteCellKey={tab.quote as selectedQuote}
                        priceQuote={priceQuote}
                        theme={theme}
                        updateLoaderCount={updateLoaderCount}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <DashboardModal
                open={!!confirmQuoteCellKey}
                onClose={handleCancel}
                title={
                    hoverJobId ? (
                        <span className="obd-hover-logo-title">
                            <span className="obd-hover-logo-title__text">Generate Quote</span>
                            <HoverLogoMark size="md" />
                        </span>
                    ) : (
                        'Generate Quote'
                    )
                }
                width="min(1280px, calc(100vw - 48px))"
                zIndex={10080}
                theme={theme}
                maskClosable={false}
                destroyOnClose
                rootClassName={`obd-theme-${theme} obd-generate-quote-modal-root obd-generate-quote-modal-root--wide`}
                className={`obd-pricing-confirm-modal obd-theme-${theme}`}
                loading={showLoadingOverlay}
                loadingMessage={loadingOverlayMessage}
                footer={
                    <div className="obd-generate-quote-footer">
                        {footerMessage && footerMessage.messages.length > 0 && loaderCount === 0 && (
                            <Alert
                                className="obd-generate-quote-footer__message"
                                message={
                                    footerMessage.messages.length === 1 ? (
                                        footerMessage.messages[0]
                                    ) : (
                                        <ul className="obd-generate-quote-footer__message-list">
                                            {footerMessage.messages.map((message) => (
                                                <li key={message}>{message}</li>
                                            ))}
                                        </ul>
                                    )
                                }
                                type={footerMessage.type}
                                showIcon
                            />
                        )}
                        <div className="obd-generate-quote-footer__actions">
                            <Space.Compact
                                className="obd-generate-quote-footer__btn-group obd-metrics__action-group"
                                role="group"
                                aria-label="Generate quote actions"
                            >
                                {showLightreachEligibleProductButton && (
                                    <Button
                                        icon={<Sun size={16} weight="duotone" />}
                                        onClick={() => setLightreachEligibleOpen(true)}
                                        disabled={loaderCount > 0}
                                    >
                                        LightReach Eligible Products
                                    </Button>
                                )}
                                {showEnfinEligibleProductButton && (
                                    <Button
                                        icon={<FolderOpen size={16} weight="duotone" />}
                                        onClick={() => setEnfinEligibleOpen(true)}
                                        disabled={loaderCount > 0}
                                    >
                                        Enfin Eligible Product
                                    </Button>
                                )}
                                <Button onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handleOk}
                                    loading={loaderCount > 0 && isGenerating}
                                    disabled={
                                        loaderCount > 0
                                        || footerMessage?.type === 'error'
                                        || (
                                            columnIncludesSolar(String(confirmQuoteCellKey?.product ?? ''))
                                            && systemDesign?.OpportunityInterest?.Solar
                                            && !(
                                                systemDesign?.DesignArray != null
                                                && systemDesign.DesignArray.length > 0
                                                && (systemDesign.DesignArray as any[])[0].ArrayId
                                            )
                                        )
                                    }
                                >
                                    Generate
                                </Button>
                            </Space.Compact>
                        </div>
                    </div>
                }
            >
                <div className="obd-generate-quote-modal-inner" style={{ position: 'relative' }}>
                    {confirmQuoteCellKey && (
                        <>
                            {tabs.length > 0 && (
                                <div className="obd-generate-quote-stack" role="region" aria-label="Generate quote by product">
                                    {tabs.map((tab) => (
                                        <div
                                            key={`${tab.key}-${tab.quote.partner}-${tab.quote.product}`}
                                            className="obd-generate-quote-stack__block"
                                        >
                                            {getTabContent(tab)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </DashboardModal>
            <EnfinEligibleProductModal
                open={enfinEligibleOpen}
                onClose={() => setEnfinEligibleOpen(false)}
                theme={theme}
                opportunityId={opportunityIdForEnfinModal}
                priceQuote={priceQuote}
                opportunityDetails={opportunityDetails}
                productType={enfinEligibleProductType}
                fetchBatteryPricing={fetchEnfinBatteryPricing}
                zIndex={10100}
            />
            <LightreachEligibleProductModal
                open={lightreachEligibleOpen}
                onClose={() => setLightreachEligibleOpen(false)}
                theme={theme}
                opportunityDetails={opportunityDetails}
                batteryPricingAmount={lightreachBatteryPricingAmount}
                preloadedBatteryEligibleRows={batteryDetails?.lightreachBatteryEligibleProducts}
                noticeApi={noticeApi}
                zIndex={10100}
            />
            <Modal
                title="Sync to Lightreach required"
                open={lightreachSyncRequiredOpen}
                zIndex={13200}
                centered
                maskClosable
                rootClassName={`obd-theme-${theme} obd-generate-quote-lightreach-sync`}
                onCancel={() => setLightreachSyncRequiredOpen(false)}
                footer={
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="primary"
                            onClick={() => setLightreachSyncRequiredOpen(false)}
                        >
                            OK
                        </Button>
                    </div>
                }
            >
                <p className="obd-generate-quote-lightreach-sync__body" style={{ margin: 0 }}>
                    {LIGHTREACH_SYNC_REQUIRED_MESSAGE}
                </p>
            </Modal>
        </>
    );
};

export default GenerateQuoteModal;
